import React, { useCallback, useEffect, useRef, useState } from "react";
import { Row, Col, Card, Button, Accordion, Dropdown, Form } from "react-bootstrap";
import ConfirmationModal from "@enerex/unified-ui-react/ConfirmationModal";
import CloseIcon from "@enerex/unified-ui-react/icons/CloseIcon";
import Div from "components/Div";
import PageTitle from "components/PageTitle/index";
import Drawer from "components/Drawer";
import FormInput from "components/FormInput/index";

import { PricingMatrixFilters } from "./components/filters/filters";
import FiltersIcon from '@enerex/unified-ui-react/icons/FilterIcon';
import ChartSection from "./components/chartSection";

import { toIsoDate, fromIsoToShort } from "./utils";

// redux
import { Provider, useDispatch, useSelector } from "react-redux";
import store from "./store";

import { set, SubmitHandler, useForm, useWatch } from "react-hook-form";

import {
  setStates,
  fetchStates,
  setSelectedState,
  setSelectedUtility,
  setSuppliers,
  fetchError,
  fetchChartsData,
  setTerms,
  setSelectedTerm,
  setFromDate,
  setToDate,
  setEffectiveFromDate,
  setEffectiveToDate,
  setStartFromDate,
  setStartToDate,
  setNoOfMonths,
  setCommodity,
  setChartsData,
  setSelectedRateClass,
  setSelectedLoadProfile,
  setSelectedLoadZone,
  setSelectedBillingMethod,
  setMarkets,
  fetchMarkets,
  setSelectedMarket,
  setUsage,
  setDemand,
  setLoadFactor,
  setLoadFactorFrom,
  setLoadFactorTo,
  setUsageFrom,
  setUsageTo,
  setProductType,
  setSelectedProductType,
  setCostComponents,
  setSelectedCostComponent,
  setCustomerClass,
} from "./store/actions/index";
import { states } from "./const/statesList";
import { termsOptions, productTypeOptions } from "./const";
// import { SelectedFilters } from "./components/filters/SelectedFilters";
import AccordionFilter from "./components/filters/AccordianFilters";
import { HyperThemeDropDown, CustomPricingState } from "./store/state";
import {
  fetchLoadZonesApi,
  fetchRateClassesApi,
  fetchLoadProfilesApi,
  fetchUtilitiesApi,
  fetchUtilitiesByStateApi,
  fetchUtilitiesByMarketApi,
  fetchStatesByUtilityApi,
  fetchBillingMethodApi,
  fetchStatesApi,
  fetchMarketsApi,
  fetchStatesByMarketApi,
  fetchSuppliersListApi,
  fetchProductTypeApi,
  fetchCostComponentsApi,
  fetchCustomerTypeApi,
  fetchTermsOptionsApi,
} from "./api";
// import StickyDiv from "components/StickyDiv";
import { useAuth } from "providers/CustomAuthProvider";
import useShowSnackbar from "hooks/useShowSnackbar";
import { saveUserFiltersApi, loadUserFiltersApi, deleteUserFilterApi } from "../../utils/userFilters";
import {
  STATIC_CUSTOM_PRICING_TRENDS,
  STATIC_CUSTOM_PRICING_TRENDS_MIN_MAX,
} from "./const/nonPremiumStaticData";

type DropdownLoadingState = {
  states: boolean;
  utilities: boolean;
  rateClasses: boolean;
  loadProfiles: boolean;
  loadZones: boolean;
  billingMethods: boolean;
  adminClients: boolean;
  costComponents: boolean;
  customerTypes: boolean;
};

const getNextMonthStartDate = () => {
  const today = new Date();
  const day = today.getDate();
  const date = new Date();
  date.setDate(1);
  date.setMonth(date.getMonth() + (day <= 15 ? 1 : 2));
  date.setHours(0, 0, 0, 0);

  return date;
};

const getYesterdayDate = () => {
  const date = new Date();
  date.setDate(date.getDate());
  date.setHours(0, 0, 0, 0);
  return date;
};

const getDealBookedDateRange = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const sixtyDaysAgo = new Date();
  sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
  sixtyDaysAgo.setHours(0, 0, 0, 0);

  return [sixtyDaysAgo, today];
};

const toIsoStringFromDate = (date: Date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const parseIsoToDate = (iso?: string | null) => {
  if (!iso) return null;
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const convertMonthSelectionToIso = (selection: any) => {
  if (!selection) return null;
  try {
    let baseDate: Date | null = null;
    if (typeof selection.toDate === "function") {
      baseDate = selection.toDate();
    } else if (selection instanceof Date) {
      baseDate = selection;
    } else if (typeof selection === "string") {
      const isoFromShort = toIsoDate(selection);
      if (isoFromShort) {
        const parsed = new Date(`${isoFromShort}T00:00:00Z`);
        if (!Number.isNaN(parsed.getTime())) {
          baseDate = new Date(
            parsed.getUTCFullYear(),
            parsed.getUTCMonth(),
            parsed.getUTCDate()
          );
        }
      } else {
        const parsed = new Date(selection);
        if (!Number.isNaN(parsed.getTime())) {
          baseDate = parsed;
        }
      }
    }

    if (!baseDate) return null;
    const firstDay = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
    return toIsoStringFromDate(firstDay);
  } catch (error) {
    console.error("Error converting month selection", error);
    return null;
  }
};

const ALL_OPTION: HyperThemeDropDown = {
  value: "All",
  name: "All",
  isSelected: false,
};

/**
 * Market to Default State Mapping
 * Maps market names to their default state names that should be auto-selected
 */
const MARKET_DEFAULT_STATES: Record<string, string> = {
  ERCOT: "Texas",
  // NYISO:"New York"
  // Add more market-to-state mappings here as needed
  // Example: "PJM": "Pennsylvania",
};

/**
 * Helper function to get the default state name for a given market
 * @param marketName - The name of the market (case-insensitive)
 * @returns The default state name for the market, or null if no mapping exists
 */
const getDefaultStateForMarket = (
  marketName: string | null | undefined
): string | null => {
  if (!marketName) return null;

  // Case-insensitive lookup
  const marketKey = Object.keys(MARKET_DEFAULT_STATES).find(
    (key) => key.toLowerCase() === marketName.toLowerCase()
  );

  return marketKey ? MARKET_DEFAULT_STATES[marketKey] : null;
};

const dateToString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const CustomPricingContent = () => {
  // const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  // const openFilterDrawer = () => setIsFilterDrawerOpen(true);
  // const closeFilterDrawer = () => setIsFilterDrawerOpen(false);

  useEffect(() => {
    document.title = "DataHub - Custom";
  }, []);

  const [availableStates, setAvailableStates] = useState<HyperThemeDropDown[]>(
    []
  );
  const [availableUtilities, setAvailableUtilities] = useState<
    HyperThemeDropDown[]
  >([]);
  const [availableRateClasses, setAvailableRateClasses] = useState<
    HyperThemeDropDown[]
  >([]);
  const [availableLoadProfiles, setAvailableLoadProfiles] = useState<
    HyperThemeDropDown[]
  >([]);
  const [availableLoadZones, setAvailableLoadZones] = useState<
    HyperThemeDropDown[]
  >([]);
  const [availableBillingMethods, setAvailableBillingMethods] = useState<
    HyperThemeDropDown[]
  >([]);
  const [availableAdminClients, setAvailableAdminClients] = useState<
    HyperThemeDropDown[]
  >([]);
  const [availableProductType, setavailableProductType] = useState<
    HyperThemeDropDown[]
  >([]);
  const [availableCustomerTypes, setAvailableCustomerTypes] = useState<
    HyperThemeDropDown[]
  >([]);
  const [dropdownLoading, setDropdownLoading] = useState<DropdownLoadingState>({
    states: false,
    utilities: false,
    rateClasses: false,
    loadProfiles: false,
    loadZones: false,
    billingMethods: false,
    adminClients: false,
    costComponents: false,
    customerTypes: false,
  });
  const hasAutoAppliedRef = useRef(false);
  const isAutoSelectingRef = useRef(false);
  const costComponentsFetchIdRef = useRef(0);
  const selectedCostComponentsRef = useRef<string[]>([]);
  const setValueRef = useRef<((name: string, value: any) => void) | null>(null); // Ref to store setValue for use in loadCostComponents
  const [effectiveResetKey, setEffectiveResetKey] = useState(0);

  // Saved filters state
  const [savedFiltersList, setSavedFiltersList] = useState<Array<{ id: string; name: string; filters: any }>>([]);
  const [selectedSavedFilterId, setSelectedSavedFilterId] = useState<string | null>(null);
  const [savingFilters, setSavingFilters] = useState(false);
  const savedFiltersLoadedRef = useRef(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveFilterName, setSaveFilterName] = useState("");

  const setDropdownLoadingState = useCallback(
    (key: keyof DropdownLoadingState, value: boolean) => {
      setDropdownLoading((prev) => {
        if (prev[key] === value) {
          return prev;
        }
        return { ...prev, [key]: value };
      });
    },
    []
  );

  const statesFetchIdRef = useRef(0);
  const utilitiesFetchIdRef = useRef(0);
  const loadZonesFetchIdRef = useRef(0);
  const rateClassesFetchIdRef = useRef(0);
  const billingMethodsFetchIdRef = useRef(0);
  const loadProfilesFetchIdRef = useRef(0);
  const adminClientsFetchIdRef = useRef(0);
  const customerTypesFetchIdRef = useRef(0);

  // Refs for State/Utility synchronization (same as Ranking page)
  const statesByUtilityFetchIdRef = useRef(0);
  const lastUtilityStateFetchRef = useRef<{ utility?: string; state?: string }>(
    { utility: undefined, state: undefined }
  );
  const lastStateUtilitiesFetchRef = useRef<{
    mode?: "state" | "market";
    state?: string;
    market?: string;
  }>({ mode: undefined, state: undefined, market: undefined });
  const prevUtilityValueRef = useRef<string | undefined>(undefined);
  const prevStateValueRef = useRef<string | undefined>(undefined);
  const prevStateObjectRef = useRef<any>(undefined);
  const prevIsTexasRef = useRef<boolean | undefined>(undefined);
  const isInitialLoadRef = useRef(true);
  const preventingCycleRef = useRef(false);
  const isMarketChangingRef = useRef(false);
  const hasInitializedFiltersRef = useRef(false);

  const dispatch = useDispatch();
  const { clientId, isSystemAdmin, accessToken, isPremium } = useAuth();
  const snackbar = useShowSnackbar();
  const pricingState = useSelector(
    (state: { customPricing: CustomPricingState }) => state.customPricing
  );
  const getSelectedClientIdentifier = useCallback(() => {
    const clientOverride = isSystemAdmin ? clientId || "None" : "None";

    return clientOverride &&
      clientOverride !== "None" &&
      clientOverride !== "current"
      ? clientOverride
      : null;
  }, [clientId, isSystemAdmin]);
  
  const isResetting = useRef(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

  const applyNonPremiumStaticCharts = useCallback(() => {
    dispatch(setChartsData(STATIC_CUSTOM_PRICING_TRENDS, "priceTrend", false));
    dispatch(
      setChartsData(
        STATIC_CUSTOM_PRICING_TRENDS_MIN_MAX,
        "priceTrendMinMax",
        false
      )
    );
  }, [dispatch]);

  // Track if we've initialized the default start date
  const hasInitializedStartDateRef = useRef(false);

  useEffect(() => {
    // Only set default start date on initial mount, not when user clears it
    if (
      !hasInitializedStartDateRef.current &&
      !pricingState.startToDate &&
      !pricingState.startFromDate
    ) {
      const defaultStartIso = toIsoStringFromDate(getNextMonthStartDate());
      dispatch(setStartFromDate(null));
      dispatch(setStartToDate(defaultStartIso));
      hasInitializedStartDateRef.current = true;
    } else if (pricingState.startToDate || pricingState.startFromDate) {
      // Mark as initialized if date is set (either by user or default)
      hasInitializedStartDateRef.current = true;
    }
    // If user clears date, don't reset it - let it stay cleared
  }, [pricingState.startToDate, pricingState.startFromDate, dispatch]);

  // Track if we've initialized the default effective date range
  const hasInitializedEffectiveDateRef = useRef(false);

  useEffect(() => {
    // Only set default range on initial mount, not when user clears it
    if (
      !hasInitializedEffectiveDateRef.current &&
      !pricingState.effectiveToDate &&
      !pricingState.effectiveFromDate
    ) {
      // Set default range: current - 60 days to current
      const [fromDate, toDate] = getDealBookedDateRange();
      const defaultEffectiveFromIso = toIsoStringFromDate(fromDate);
      const defaultEffectiveToIso = toIsoStringFromDate(toDate);
      dispatch(setEffectiveFromDate(defaultEffectiveFromIso));
      dispatch(setEffectiveToDate(defaultEffectiveToIso));
      hasInitializedEffectiveDateRef.current = true;
    } else if (pricingState.effectiveToDate || pricingState.effectiveFromDate) {
      // Mark as initialized if dates are set (either by user or default)
      hasInitializedEffectiveDateRef.current = true;
    }
  }, [pricingState.effectiveToDate, pricingState.effectiveFromDate, dispatch]);
  // on mount only
  useEffect(() => {
    if (!accessToken) return;
    //  fetchStatesApi({ userToken: accessToken })
    //     .then(setAvailableStates)
    //     .catch(console.error);

    const loadBillingMethods = async () => {
      const requestId = ++billingMethodsFetchIdRef.current;
      setDropdownLoadingState("billingMethods", true);
      try {
        const list = await fetchBillingMethodApi({ userToken: accessToken });
        if (billingMethodsFetchIdRef.current === requestId) {
          setAvailableBillingMethods(list);
        }
      } catch (error) {
        console.error("Error loading billing methods", error);
        if (billingMethodsFetchIdRef.current === requestId) {
          setAvailableBillingMethods([]);
        }
      } finally {
        if (billingMethodsFetchIdRef.current === requestId) {
          setDropdownLoadingState("billingMethods", false);
        }
      }
    };

    const loadLoadProfiles = async () => {
      const requestId = ++loadProfilesFetchIdRef.current;
      setDropdownLoadingState("loadProfiles", true);
      try {
        const list = await fetchLoadProfilesApi({ userToken: accessToken });
        if (loadProfilesFetchIdRef.current === requestId) {
          setAvailableLoadProfiles(list);
        }
      } catch (error) {
        console.error("Error loading load profiles", error);
        if (loadProfilesFetchIdRef.current === requestId) {
          setAvailableLoadProfiles([]);
        }
      } finally {
        if (loadProfilesFetchIdRef.current === requestId) {
          setDropdownLoadingState("loadProfiles", false);
        }
      }
    };

    const loadAdminClients = async () => {
      const requestId = ++adminClientsFetchIdRef.current;
      setDropdownLoadingState("adminClients", true);
      try {
        const list = await fetchSuppliersListApi({ userToken: accessToken });
        if (adminClientsFetchIdRef.current === requestId) {
          // Add "None" as the first option
          const noneOption = {
            id: "None",
            value: "None",
            name: "None",
            isSelected: false,
          };
          setAvailableAdminClients([noneOption, ...list]);
        }
      } catch (error) {
        console.error("Error loading admin clients", error);
        if (adminClientsFetchIdRef.current === requestId) {
          // Even on error, include "None" option
          const noneOption = {
            id: "None",
            value: "None",
            name: "None",
            isSelected: false,
          };
          setAvailableAdminClients([noneOption]);
        }
      } finally {
        if (adminClientsFetchIdRef.current === requestId) {
          setDropdownLoadingState("adminClients", false);
        }
      }
    };

    const loadCustomerTypes = async () => {
      const requestId = ++customerTypesFetchIdRef.current;
      setDropdownLoadingState("customerTypes", true);
      try {
        const list = await fetchCustomerTypeApi({ userToken: accessToken });
        if (customerTypesFetchIdRef.current === requestId) {
          setAvailableCustomerTypes(list);
        }
      } catch (error) {
        console.error("Error loading customer types", error);
        if (customerTypesFetchIdRef.current === requestId) {
          setAvailableCustomerTypes([]);
        }
      } finally {
        if (customerTypesFetchIdRef.current === requestId) {
          setDropdownLoadingState("customerTypes", false);
        }
      }
    };

    loadBillingMethods();
    loadLoadProfiles();
    loadCustomerTypes();

    // fetchProductTypeApi({})
    //   .then((productTypes) => {
    //     setavailableProductType(productTypes);
    //   })
    //   .catch((err) => console.error("Error loading product types", err));

    // Load all available markets (no commodity filter)
    fetchMarketsApi({ userToken: accessToken })
      .then((markets) => {
        dispatch(setMarkets(markets));
      })
      .catch(console.error);

    if (isSystemAdmin) {
      loadAdminClients();
    } else {
      setAvailableAdminClients([]);
      setDropdownLoadingState("adminClients", false);
    }
  }, [accessToken, isSystemAdmin, dispatch, setDropdownLoadingState]);

  const DEFAULT_PRODUCT_TYPE_VALUE = "Fixed";
  const defaultProductTypeOption = {
    name: "Fixed",
    value: DEFAULT_PRODUCT_TYPE_VALUE,
  };
  const getDefaultProductTypeSelection = () => [
    { ...defaultProductTypeOption },
  ];

  // Terms field is now a number input, no API call needed
  // Default term value is set in defaultValues

  useEffect(() => {
    if (!accessToken) return;
    // Wait until a market is selected before loading cost components
    if (!pricingState.selectedMarket?.commodity) return;

    const resolveCommodityLabel = () => {
      // const explicitCommodity = pricingState.commodity;
      // if (explicitCommodity === 1 ) return "Electric";
      // if (explicitCommodity === 2 ) return "Gas";

      const marketCommodity = pricingState.selectedMarket?.commodity;
      if (!marketCommodity) return "Electric";
      return String(marketCommodity).toLowerCase().includes("gas") ||
        String(marketCommodity).toLowerCase().includes("natgas")
        ? "NatGas"
        : "Electric";
    };

    const loadCostComponents = async () => {
      const requestId = ++costComponentsFetchIdRef.current;
      const market = resolveCommodityLabel();
      setDropdownLoadingState("costComponents", true);
      try {
        const list = await fetchCostComponentsApi({
          userToken: accessToken,
          commodity: market,
        });
        if (costComponentsFetchIdRef.current === requestId) {
          // Only preserve selections if they exist in the ref (not when market changes)
          const selectedSet = new Set(selectedCostComponentsRef.current);
          let normalized =
            list?.map((item: any) => ({
              ...item,
              isSelected: selectedSet.has(item.value),
            })) || [];

          // On initial load (no previous selection), auto-select "All"
          // so that Pass Through shows "All" and badge is visible.
          if (selectedSet.size === 0 && normalized.length > 0) {
            const allIndex = normalized.findIndex(
              (cc: any) => cc.value === "All"
            );
            if (allIndex !== -1) {
              normalized = normalized.map((cc: any, idx: number) =>
                idx === allIndex ? { ...cc, isSelected: true } : cc
              );
              // Track default selection in the ref so future loads preserve it
              selectedCostComponentsRef.current = ["All"];
              // Sync the form field so the UI shows "All" selected
              const allItem = normalized[allIndex];
              // Use setValueRef if available (setValue might not be in scope)
              if (setValueRef.current) {
                setValueRef.current("passThrough", [
                  {
                    id: allItem.value,
                    name: allItem.name,
                    value: allItem.value,
                  },
                ]);
              }
            }
          }

          dispatch(setCostComponents(normalized));
        }
      } catch (error) {
        console.error("Error loading cost components", error);
        if (costComponentsFetchIdRef.current === requestId) {
          dispatch(setCostComponents([]));
        }
      } finally {
        if (costComponentsFetchIdRef.current === requestId) {
          setDropdownLoadingState("costComponents", false);
        }
      }
    };

    loadCostComponents();
  }, [
    accessToken,
    pricingState.commodity,
    pricingState.selectedMarket?.commodity,
    pricingState.selectedMarket, // Add selectedMarket to ensure effect runs when market changes
    dispatch,
    setDropdownLoadingState,
  ]);

  const {
    handleSubmit,
    register,
    control,
    formState: { errors, dirtyFields },
    reset,
    setValue,
    getValues,
    trigger,
  } = useForm<any>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: {
      commodity: "1",
      stateCode: { id: "All", name: "All" },
      toDate: "",
      fromDate: "",
      effectiveDate: getDealBookedDateRange(),
      startToDate: getNextMonthStartDate(),
      noOfMonth: { id: "12", name: "12", value: "12" },
      term: null,
      utility: { id: "All", name: "All", value: "All" },
      rateClass: { id: "None", value: "None", name: "None" },
      loadProfile: { id: "All", value: "All", name: "All" },
      loadZone: { id: "All", value: "All", name: "All" },
      billingMethod: { id: "All", value: "All", name: "All" },
      usage: null,
      usageFrom: null,
      usageTo: null,
      demand: null,
      loadFactor: null,
      productType: getDefaultProductTypeSelection(),
      // Prefill Pass Through with default "All"
      passThrough: [{ id: "All", name: "All", value: "All" }],
      customerType: {
        id: "Commercial",
        name: "Commercial",
        value: "Commercial",
      },
      showAll: false,
      clientOverride: { id: "None", value: "None", name: "None" },
    },
  });

  // Store setValue in ref so it can be used in loadCostComponents effect
  setValueRef.current = setValue;

  // Set default "None" value for clientOverride when adminClients loads
  useEffect(() => {
    if (availableAdminClients.length > 0 && setValue) {
      const currentValue = getValues("clientOverride");
      const noneOption = availableAdminClients.find(
        (client: any) => client.value === "None"
      ) as any;
      // Set to "None" if not already set, or if current value is not "None"
      if (
        noneOption &&
        (!currentValue || !currentValue.value || currentValue.value !== "None")
      ) {
        setValue("clientOverride", {
          id: noneOption.id || noneOption.value,
          value: noneOption.value,
          name: noneOption.name,
        });
      }
    }
  }, [availableAdminClients, setValue, getValues]);

  const watchedState = useWatch({ control, name: "stateCode" });
  const watchedCommodity = useWatch({ control, name: "commodity" });
  const watchedUtility = useWatch({ control, name: "utility" });
  const watchedLoadProfile = useWatch({ control, name: "loadProfile" });
  const selectedMarket = pricingState.selectedMarket;

  useEffect(() => {
    return () => {
      dispatch(setSelectedCostComponent([]));
      selectedCostComponentsRef.current = [];
    };
  }, [dispatch]);

  /**
   * fetchStatesForMarket
   *
   * Purpose: Fetches states for the selected market.
   *
   * When called:
   * - On initial page load when market is selected
   * - When user changes market selection
   * - When utility changes to "All" (user action) to reload states from market
   *
   * API: POST /states/market
   * Body: { marketEnerexIdentifier, commodity }
   *
   * Returns: List of states for the selected market
   */
  const fetchStatesForMarket = useCallback(async (): Promise<{
    list: HyperThemeDropDown[];
    applied: boolean;
  }> => {
    // Ensure we have a valid selected market
    // debugger;
    if (!selectedMarket) {
      console.warn("fetchStatesForMarket: selectedMarket is not available");
      setDropdownLoadingState("states", false);
      setAvailableStates([]);
      dispatch(setStates([]));
      return { list: [], applied: true };
    }

    // Get market identifier - should be EnerexIdentifier stored in value property
    const marketEnerexIdentifier = selectedMarket.value;
    const commodityLabel =
      selectedMarket?.commodity &&
      selectedMarket.commodity.toLowerCase().includes("gas")
        ? "NatGas"
        : "Electric";

    // Log for debugging
    // console.log("fetchStatesForMarket called with:", {
    //   marketName: selectedMarket.name,
    //   marketIdentifier: marketEnerexIdentifier,
    //   commodity: commodityLabel,
    //   selectedMarket: selectedMarket
    // });

    if (!accessToken || !marketEnerexIdentifier) {
      // console.warn("fetchStatesForMarket: Missing accessToken or marketEnerexIdentifier", {
      //   hasAccessToken: !!accessToken,
      //   marketEnerexIdentifier
      // });
      setDropdownLoadingState("states", false);
      setAvailableStates([]);
      dispatch(setStates([]));
      return { list: [], applied: true };
    }
    const requestId = ++statesFetchIdRef.current;
    setDropdownLoadingState("states", true);
    try {
      const list = await fetchStatesByMarketApi({
        marketEnerexIdentifier,
        commodity: commodityLabel,
        userToken: accessToken,
      });
      const applied = statesFetchIdRef.current === requestId;
      if (applied) {
        // Validate that we got market-specific states, not all states
        // ERCOT should only have a few states (mainly Texas), not all 50 states
        const statesCount =
          list?.filter((s: any) => s?.name && s.name !== "All")?.length || 0;
        if (statesCount > 20) {
          console.warn(
            "WARNING: Received too many states for market. This might indicate all states were loaded instead of market-specific states.",
            {
              marketName: selectedMarket.name,
              marketIdentifier: marketEnerexIdentifier,
              statesCount: statesCount,
              firstFewStates: list?.slice(0, 5).map((s: any) => s.name),
            }
          );
        }

        setAvailableStates(list);
        dispatch(setStates(list));
      }
      return { list, applied };
    } catch (error) {
      console.error("Error loading states by market", error);
      const applied = statesFetchIdRef.current === requestId;
      if (applied) {
        setAvailableStates([]);
        dispatch(setStates([]));
      }
      return { list: [], applied };
    } finally {
      if (statesFetchIdRef.current === requestId) {
        setDropdownLoadingState("states", false);
      }
    }
  }, [accessToken, selectedMarket, dispatch, setDropdownLoadingState]);

  /**
   * fetchUtilitiesForState
   *
   * Purpose: Fetches utilities based on a specific state selection.
   *
   * When called:
   * - When user selects a specific state (not "All")
   * - During initial load if ERCOT auto-selects Texas
   *
   * API: POST /utility/bystate
   * Body: { enerexStateIdentifier, commodity }
   *
   * Returns: List of utilities for the selected state
   */
  const fetchUtilitiesForState = useCallback(
    async (stateIdentifier?: string): Promise<HyperThemeDropDown[]> => {
      const commodityLabel =
        selectedMarket?.commodity &&
        selectedMarket.commodity.toLowerCase().includes("gas")
          ? "NatGas"
          : "Electric";

      if (!accessToken || !stateIdentifier || stateIdentifier === "All") {
        const defaults = [ALL_OPTION];
        setAvailableUtilities(defaults);
        setDropdownLoadingState("utilities", false);
        return defaults;
      }

      const requestId = ++utilitiesFetchIdRef.current;
      setDropdownLoadingState("utilities", true);
      try {
        const list = await fetchUtilitiesByStateApi({
          stateIdentifier,
          commodity: commodityLabel,
          userToken: accessToken,
        });
        const hasAll = list?.some((item: any) => item?.value === "All");
        const finalList = hasAll ? list : [ALL_OPTION, ...(list || [])];
        if (utilitiesFetchIdRef.current === requestId) {
          setAvailableUtilities(finalList);
        }
        return finalList;
      } catch (error) {
        console.error("Error loading utilities by state", error);
        if (utilitiesFetchIdRef.current === requestId) {
          setAvailableUtilities([ALL_OPTION]);
        }
        return [ALL_OPTION];
      } finally {
        if (utilitiesFetchIdRef.current === requestId) {
          setDropdownLoadingState("utilities", false);
        }
      }
    },
    [accessToken, selectedMarket, setDropdownLoadingState]
  );

  /**
   * fetchStatesForUtilitySelection
   *
   * Purpose: Fetches states based on a specific utility selection.
   *
   * When called:
   * - When user selects a specific utility (not "All")
   *
   * API: POST /states/byutility
   * Body: { enerexUtilityIdentifiers, commodity }
   *
   * Returns: List of states for the selected utility
   */
  const fetchStatesForUtilitySelection = useCallback(
    async (
      utilityIdentifier?: string,
      currentStateValue?: string
    ): Promise<HyperThemeDropDown[]> => {
      const commodityLabel =
        selectedMarket?.commodity &&
        selectedMarket.commodity.toLowerCase().includes("gas")
          ? "NatGas"
          : "Electric";

      if (!accessToken || !utilityIdentifier || utilityIdentifier === "All") {
        return availableStates;
      }

      const requestId = ++statesByUtilityFetchIdRef.current;
      setDropdownLoadingState("states", true);
      try {
        const list = await fetchStatesByUtilityApi({
          utilityIdentifier,
          commodity: commodityLabel,
          userToken: accessToken,
        });
        const hasAll = list?.some((item: any) => item?.value === "All");
        const finalList = hasAll ? list : [ALL_OPTION, ...(list || [])];
        if (statesByUtilityFetchIdRef.current === requestId) {
          setAvailableStates(finalList);
          dispatch(setStates(finalList));
          if (
            currentStateValue &&
            !finalList.some((item) => item.value === currentStateValue)
          ) {
            dispatch(setSelectedState(null));
            setValue("stateCode", { id: "All", name: "All", value: "All" });
          }
        }
        return finalList;
      } catch (error) {
        console.error("Error loading states by utility", error);
        if (statesByUtilityFetchIdRef.current === requestId) {
          const defaults = [ALL_OPTION];
          setAvailableStates(defaults);
          dispatch(setStates(defaults));
          dispatch(setSelectedState(null));
          setValue("stateCode", { id: "All", name: "All", value: "All" });
        }
        return [ALL_OPTION];
      } finally {
        if (statesByUtilityFetchIdRef.current === requestId) {
          setDropdownLoadingState("states", false);
        }
      }
    },
    [
      accessToken,
      selectedMarket,
      dispatch,
      availableStates,
      setDropdownLoadingState,
      setValue,
    ]
  );

  const fetchLoadZonesForState = useCallback(
    async (
      stateIdentifier?: string,
      skip?: boolean
    ): Promise<HyperThemeDropDown[]> => {
      const requestId = ++loadZonesFetchIdRef.current;
      if (skip) {
        setAvailableLoadZones([]);
        setDropdownLoadingState("loadZones", false);
        return [];
      }
      if (!accessToken || !stateIdentifier) {
        setAvailableLoadZones([]);
        setDropdownLoadingState("loadZones", false);
        return [];
      }
      setDropdownLoadingState("loadZones", true);
      try {
        const list = await fetchLoadZonesApi({
          stateIdentifier,
          userToken: accessToken,
        });
        if (loadZonesFetchIdRef.current === requestId) {
          setAvailableLoadZones(list);
          return list;
        }
        return [];
      } catch (error) {
        console.error("Error loading load zones", error);
        if (loadZonesFetchIdRef.current === requestId) {
          setAvailableLoadZones([]);
        }
        return [];
      } finally {
        if (loadZonesFetchIdRef.current === requestId) {
          setDropdownLoadingState("loadZones", false);
        }
      }
    },
    [accessToken, setDropdownLoadingState]
  );

  // Helper function to set "None" option for Rate Class when there are no options
  // const setRateClassToNone = useCallback(() => {
  //   const noneOption = {
  //     id: "None",
  //     name: "None",
  //     value: "None",
  //   } as any;
  //   setAvailableRateClasses([{
  //     name: "None",
  //     value: "None",
  //     isSelected:false
  //   }]);
  //   debugger;
  //   dispatch(setSelectedRateClass({ value: "None", name: "None" }));
  //   setValue("rateClass", {
  //       id: "None",
  //       name: "None",
  //       value: "None",
  //     });
  //   if (setValueRef.current) {
  //     setValueRef.current("rateClass", {
  //       id: "None",
  //       name: "None",
  //       value: "None",
  //     });
  //   }
  // }, [dispatch]);

  const setRateClassToNone = useCallback(() => {
    const noneOption = {
      id: "None",
      name: "None",
      value: "None",
      isSelected: true, // ✅ THIS IS THE KEY
    };
    setAvailableRateClasses([
      {
        name: "None",
        value: "None",
        isSelected: true, // ✅ THIS IS THE KEY
      },
    ]);
    dispatch(setSelectedRateClass({ value: "None", name: "None" }));

    // 1️⃣ Update options with only "None" selected
    // setAvailableRateClasses([noneOption]);

    // 2️⃣ Sync redux (optional, but consistent)

    // 3️⃣ Sync React Hook Form
    const formValue = {
      id: "None",
      name: "None",
      value: "None",
    };

    setValue("rateClass", formValue);

    if (setValueRef.current) {
      setValueRef.current("rateClass", formValue);
    }
  }, [dispatch, setValue]);

  const fetchRateClassesForUtility = useCallback(
    async (
      utilityIdentifier: string,
      commodity: number | string
    ): Promise<HyperThemeDropDown[]> => {
      // debugger;
      const requestId = ++rateClassesFetchIdRef.current;
      if (!accessToken || !utilityIdentifier || utilityIdentifier === "All") {
        setRateClassToNone();
        setDropdownLoadingState("rateClasses", false);
        return [];
      }

      // Store current rate class value BEFORE API call to preserve if it exists in new list
      const currentRateClassValue = getValues("rateClass");
      const previousRateClassValue = currentRateClassValue?.value;

      setDropdownLoadingState("rateClasses", true);
      try {
        const list = await fetchRateClassesApi({
          utilityIdentifier,
          commodity,
          userToken: accessToken,
        });
        if (rateClassesFetchIdRef.current === requestId) {
          // If there are no real rate classes (only the synthetic "All" option),
          // show "None" in the UI instead of "All".
          // Check if list is empty OR only contains "All" option(s)
          const realRateClasses = Array.isArray(list)
            ? list.filter((item: any) => item?.value && item.value !== "All")
            : [];

          const hasOnlyAllOption =
            !Array.isArray(list) ||
            list.length === 0 ||
            realRateClasses.length === 0;

          if (hasOnlyAllOption) {
            // API returned empty/null - set to "None"
            const noneOption = {
              id: "None",
              name: "None",
              value: "None",
            } as any;
            setRateClassToNone();
            return [noneOption];
          }

          // API returned values - try to preserve previous value if it exists in new list
          setAvailableRateClasses(list);
          // Ensure form has a default value when real options are available
          // If no value is set, default to "All"
          // debugger;
          const currentRateClassValue = getValues("rateClass");
          if (
            !currentRateClassValue ||
            currentRateClassValue.value === "None"
          ) {
            if (setValueRef.current) {
              setValueRef.current("rateClass", {
                id: "All",
                name: "All",
                value: "All",
              });
            }
            dispatch(setSelectedRateClass({ value: "All", name: "All" }));
          }
          return list;
        }
        return [];
      } catch (error) {
        console.error("Error loading rate classes", error);
        if (rateClassesFetchIdRef.current === requestId) {
          setRateClassToNone();
        }
        return [];
      } finally {
        if (rateClassesFetchIdRef.current === requestId) {
          setDropdownLoadingState("rateClasses", false);
        }
      }
    },
    [
      accessToken,
      setDropdownLoadingState,
      dispatch,
      setRateClassToNone,
      getValues,
      setValue,
    ]
  );

  // When market changes, load states scoped to market
  useEffect(() => {
    if (!accessToken) return;
    if (!selectedMarket?.name) {
      setDropdownLoadingState("states", false);
      return;
    }
    // Set market changing flag to prevent state watcher from interfering
    isMarketChangingRef.current = true;

    // Check if the selected market is gas (handle various commodity naming conventions)
    const isGasMarket =
      selectedMarket.commodity &&
      (selectedMarket.commodity.toLowerCase().includes("gas") ||
        selectedMarket.commodity.toLowerCase().includes("natgas"));

    // Clear dependent selections and inputs for the new market
    setAvailableUtilities([]);
    setavailableProductType([]);
    setDropdownLoadingState("utilities", false);
    setDropdownLoadingState("rateClasses", false);

    // Load product types when market changes
    const resolveCommodityNumeric = () => {
      if (!selectedMarket?.commodity) return 1; // default to Electric
      return String(selectedMarket.commodity).toLowerCase().includes("gas") ||
        String(selectedMarket.commodity).toLowerCase().includes("natgas")
        ? 2
        : 1;
    };

    const loadProductTypesOnMarketChange = async () => {
      const commodityValue = resolveCommodityNumeric();
      try {
        const productTypesList = await fetchProductTypeApi({
          userToken: accessToken,
          commodity: commodityValue,
        });

        // Find "Fixed" option - when market changes, always reset to "Fixed" only
        const fixedOption = productTypesList.find(
          (pt: any) =>
            pt.value === DEFAULT_PRODUCT_TYPE_VALUE ||
            pt.name === DEFAULT_PRODUCT_TYPE_VALUE
        );

        // When market changes, always select only "Fixed" (don't preserve previous selections)
        let selectedValues: string[] = [];
        if (fixedOption) {
          const fixedValue = fixedOption.value || fixedOption.name;
          selectedValues = [fixedValue];
        } else if (productTypesList.length > 0) {
          // If "Fixed" doesn't exist, select the first one
          const firstOption = productTypesList[0];
          const firstValue = firstOption.value || firstOption.name;
          selectedValues = [firstValue];
        }

        // Transform to include isSelected property based on selectedValues
        const normalizedProductTypes = productTypesList.map((pt: any) => {
          const ptValue = pt.value || pt.name;
          return {
            ...pt,
            isSelected: selectedValues.includes(ptValue),
          };
        });

        dispatch(setProductType(normalizedProductTypes));

        // Dispatch selected product types
        if (selectedValues.length > 0) {
          dispatch(setSelectedProductType(selectedValues));
          // Also set form value using setValueRef
          if (setValueRef.current) {
            const selectedOptions = normalizedProductTypes
              .filter((pt: any) => pt.isSelected)
              .map((pt: any) => ({
                id: pt.id,
                value: pt.value || pt.name,
                name: pt.name,
              }));
            setValueRef.current("productType", selectedOptions);
          }
        }
      } catch (error) {
        console.error("Error loading product types on market change", error);
        // On error, keep using static options (fallback)
        dispatch(
          setProductType(
            productTypeOptions.map((pt: any) => ({
              ...pt,
              isSelected: pt.value === DEFAULT_PRODUCT_TYPE_VALUE,
            }))
          )
        );
      }
    };

    loadProductTypesOnMarketChange();

    if (isGasMarket) {
      setAvailableLoadZones([]);
      setDropdownLoadingState("loadZones", false);
    }

    dispatch(setSelectedState(null));
    dispatch(setSelectedUtility(null));
    // dispatch(setSelectedRateClass({  name: "None", value: "None" }));
    dispatch(setSelectedLoadProfile(null));
    dispatch(setSelectedLoadZone(null));
    dispatch(setSelectedBillingMethod(null));
    dispatch(setSelectedCostComponent([]));

    // Reset form fields to 'All' for the new market context
    setValue("stateCode", { id: "All", name: "All", value: "All" });
    setValue("utility", { id: "All", name: "All", value: "All" });
    // setValue("rateClass", { id: "None", name: "None", value: "None" });
    setValue("loadProfile", { id: "All", name: "All", value: "All" });
    setValue("loadZone", { id: "All", name: "All", value: "All" });
    setValue("billingMethod", { id: "All", name: "All", value: "All" });
    setValue("passThrough", []);

    // Clear cost component selections when market changes
    selectedCostComponentsRef.current = [];
    // debugger;
    const loadStatesAndDependents = async () => {
      try {
        // Ensure we have a valid selected market before proceeding
        if (!selectedMarket?.name || !selectedMarket?.value) {
          console.warn("Selected market is not available for initial load", {
            selectedMarket: selectedMarket,
          });
          return;
        }

        // Clear states before fetching to prevent old data from persisting
        setAvailableStates([]);
        dispatch(setStates([]));
        setDropdownLoadingState("states", true);

        const { applied, list } = await fetchStatesForMarket();
        if (!applied) {
          console.warn(
            "fetchStatesForMarket did not apply - request was superseded"
          );
          return;
        }

        // console.log("States loaded for market:", {
        //   marketName: selectedMarket.name,
        //   statesCount: list?.length,
        //   firstFewStates: list?.slice(0, 3).map((s: any) => s.name)
        // });

        const validStates = list.filter(
          (state) => state?.name && state.name !== "All"
        );

        // Check if market is ERCOT and Texas is in the list
        // const isErcotMarket = selectedMarket.name === "ERCOT" || selectedMarket.name?.toLowerCase() === "ercot";
        // const texasState = validStates.find((state) => state.name === "Texas");

        // Get default state for the selected market from constant mapping
        const defaultStateName = getDefaultStateForMarket(selectedMarket.name);
        const defaultState = defaultStateName
          ? validStates.find((state) => state.name === defaultStateName)
          : null;

        let stateToSelect = null;

        if (validStates.length === 1 && defaultStateName) {
          // Auto-select if only one state
          stateToSelect = validStates[0];
        } else if (defaultState) {
          // Auto-select Texas if ERCOT market and Texas is available
          stateToSelect = defaultState;
        }

        if (stateToSelect) {
          isAutoSelectingRef.current = true;

          dispatch(
            setSelectedState({
              value: stateToSelect.value,
              name: stateToSelect.name,
            })
          );
          setValue("stateCode", {
            id: stateToSelect.value,
            name: stateToSelect.name,
            value: stateToSelect.value,
          });

          // Load utilities based on selected state (using state identifier)
          const utilitiesList = await fetchUtilitiesForState(
            stateToSelect.value
          );
          setAvailableUtilities(utilitiesList);

          // Load load zones if not a gas market and state is Texas
          if (!isGasMarket) {
            setDropdownLoadingState("loadZones", true);
            fetchLoadZonesApi({
              stateIdentifier: stateToSelect.value,
              userToken: accessToken,
            })
              .then(setAvailableLoadZones)
              .catch(console.error)
              .finally(() => setDropdownLoadingState("loadZones", false));
          } else {
            setAvailableLoadZones([]);
            setDropdownLoadingState("loadZones", false);
          }

          isAutoSelectingRef.current = false;
        } else {
          // No auto-selection, load market-based utilities
          const marketIdentifier = selectedMarket?.value;
          const commodityLabel =
            selectedMarket?.commodity &&
            selectedMarket.commodity.toLowerCase().includes("gas")
              ? "NatGas"
              : "Electric";
          if (!marketIdentifier) {
            console.warn(
              "Market identifier not available for loading utilities",
              {
                selectedMarket: selectedMarket,
              }
            );
            setAvailableUtilities([]);
          } else {
            setDropdownLoadingState("utilities", true);
            fetchUtilitiesByMarketApi({
              marketIdentifier,
              commodity: commodityLabel,
              userToken: accessToken,
            })
              .then((utilitiesList) => {
                setAvailableUtilities(utilitiesList);
              })
              .catch((err) => {
                console.error("Error loading utilities for market", {
                  marketIdentifier,
                  marketName: selectedMarket?.name,
                  error: err,
                });
              })
              .finally(() => setDropdownLoadingState("utilities", false));
          }

          dispatch(setSelectedState(null));
          setValue("stateCode", { id: "All", name: "All", value: "All" });
          isAutoSelectingRef.current = false;
        }

        // Mark initial load as complete after states and utilities are loaded
        setRateClassToNone(); // Set "None" when market changes

        isInitialLoadRef.current = false;

        setIsInitialLoadComplete(true);
      } catch (error) {
        console.error("Error loading states by market", error);
        dispatch(setSelectedState(null));
        setValue("stateCode", { id: "All", name: "All", value: "All" });
        isAutoSelectingRef.current = false;
        isInitialLoadRef.current = false;
        setIsInitialLoadComplete(true);
      } finally {
        // Reset market changing flag after loadStatesAndDependents completes
        isMarketChangingRef.current = false;
      }
    };

    loadStatesAndDependents();
  }, [
    selectedMarket,
    accessToken,
    dispatch,
    fetchStatesForMarket,
    fetchUtilitiesForState,
    fetchUtilitiesByMarketApi,
    fetchLoadZonesApi,
    setDropdownLoadingState,
    setValue,
  ]);

  // Note: Do not auto-fetch charts on market select; charts load only on Apply

  // Auto-apply once on initial load when token, selected market, and its states are ready
  // Also auto-apply when we have auto-selected a state from a single-state market
  useEffect(() => {
    const marketReady = Boolean(pricingState?.selectedMarket?.name);
    const statesLoading = dropdownLoading.states;
    const statesReady =
      Array.isArray(availableStates) &&
      availableStates.filter((s) => s?.name && s.name !== "All").length > 0;
    const stateSelected = Boolean(
      pricingState?.selectedState?.name &&
        pricingState.selectedState.name !== "All"
    );

    if (
      !hasAutoAppliedRef.current &&
      accessToken &&
      marketReady &&
      !statesLoading && // Ensure states are not loading
      isInitialLoadComplete && // Ensure initial load is complete
      isPremium !== null // Wait for premium flag resolution
    ) {
      hasAutoAppliedRef.current = true;
      if (isPremium === true) {
        const selectedClientIdentifier = getSelectedClientIdentifier();
        dispatch(
          fetchChartsData(
            clientId,
            selectedClientIdentifier,
            accessToken,
            false,
            isSystemAdmin,
            isPremium
          )
        );
      } else {
        applyNonPremiumStaticCharts();
      }
    }
  }, [
    pricingState?.selectedMarket,
    pricingState?.selectedState,
    availableStates,
    accessToken,
    dropdownLoading.states, // Re-run when states loading completes
    isInitialLoadComplete,
    isPremium,
    applyNonPremiumStaticCharts,
    clientId,
    getSelectedClientIdentifier,
    isSystemAdmin,
  ]);

  // Note: Do not force sync of effectiveDate from Redux into the form during selection

  /**
   * State Watcher Effect
   *
   * Purpose: Handles state dropdown changes and updates utility dropdown accordingly.
   *
   * When called:
   * - On initial load (market-based loading)
   * - When user selects a specific state
   * - When user changes state to "All"
   *
   * Behavior:
   * 1. If both state and utility are "All" → Load utilities from market API
   * 2. If specific state selected → Load utilities from state API (POST /utility/bystate)
   * 3. If state is "All" → Load utilities from market API
   * 4. Also loads load zones when specific state is selected (for non-gas markets)
   *
   * Note: Only clears/reloads utilities when state value actually changes to prevent unnecessary API calls
   */
  useEffect(() => {
    // Skip during auto-selection or initial load to prevent interference
    if (
      isAutoSelectingRef.current ||
      isInitialLoadRef.current ||
      isMarketChangingRef.current
    ) {
      return;
    }

    if (!watchedState || watchedCommodity == null || !accessToken) {
      setAvailableUtilities([]);
      setDropdownLoadingState("utilities", false);
      setDropdownLoadingState("loadZones", false);
      return;
    }
    // debugger;
    // Extract state value (handle both string and object formats)
    const currentStateValueRaw = watchedState?.value;
    const currentStateValue =
      typeof currentStateValueRaw === "object" && currentStateValueRaw !== null
        ? currentStateValueRaw?.value || currentStateValueRaw?.id || null
        : currentStateValueRaw;

    // Normalize "All" values for "All" check
    const normalizedCurrentState =
      !currentStateValue ||
      currentStateValue === "All" ||
      currentStateValue === null
        ? "All"
        : currentStateValue;
    const currentStateIsAll = normalizedCurrentState === "All";

    // Check if state object reference changed (detects user interaction even if value is same)
    const stateObjectChanged = prevStateObjectRef.current !== watchedState;

    // Check if state actually changed by comparing current normalized value with previous
    // Use a more reliable comparison that handles undefined -> "All" transitions
    const prevStateRaw = prevStateValueRef.current;
    const normalizedPrevState =
      !prevStateRaw || prevStateRaw === null ? "" : prevStateRaw;
    const prevStateWasAll = normalizedPrevState === "All";

    // State changed if normalized values are different OR if previous was undefined and current is "All"
    // OR if state object reference changed AND current state is "All" (user explicitly selected "All" from dropdown)
    const stateChanged =
      normalizedPrevState !== normalizedCurrentState ||
      (prevStateRaw === undefined && normalizedCurrentState === "All") ||
      (stateObjectChanged && currentStateIsAll && !isInitialLoadRef.current);

    const stateChangedToAll = !prevStateWasAll && currentStateIsAll;

    // Check if Texas state changed (for Load Profile/Rate Class switching)
    const isTexas = watchedState?.name === "Texas";
    const prevIsTexas = prevIsTexasRef.current;
    const texasChanged = prevIsTexas !== undefined && prevIsTexas !== isTexas;

    // Update refs for next comparison
    prevStateValueRef.current = normalizedCurrentState;
    prevStateObjectRef.current = watchedState;
    prevIsTexasRef.current = isTexas;

    if (isResetting.current) {
      isResetting.current = false;
    } else if (stateChanged) {
      // Only clear utilities when state actually changes
      setAvailableUtilities([]);
      setRateClassToNone(); // Set "None" when state changes
      setDropdownLoadingState("rateClasses", false);

      // Handle Rate Class/Load Profile switching when state changes from Texas to non-Texas or vice versa
      if (texasChanged && prevIsTexas !== undefined) {
        if (isTexas) {
          // Switching to Texas: Clear Rate Class, show Load Profile
          dispatch(setSelectedRateClass(null));
          setValue("rateClass", { id: "All", name: "All", value: "All" });
          setAvailableRateClasses([]); // Clear for Texas (Load Profile is shown instead)
          setDropdownLoadingState("rateClasses", false);
        } else if (prevIsTexas) {
          // Switching from Texas to non-Texas: Clear Load Profile, show Rate Class
          dispatch(setSelectedLoadProfile(null));
          setValue("loadProfile", { id: "All", name: "All", value: "All" });

          // Rate class API will be called from utility watcher after state API completes
          // No need to call it here in state watcher
          setRateClassToNone(); // Set "None" when switching from Texas
          setDropdownLoadingState("rateClasses", false);
        }
      }
    }

    const isGasMarket =
      selectedMarket?.commodity &&
      (selectedMarket.commodity.toLowerCase().includes("gas") ||
        selectedMarket.commodity.toLowerCase().includes("natgas"));

    const marketIdentifier = selectedMarket?.value;
    const isStateAll = currentStateIsAll;
    // Check if utility is "All" - handle both string and object formats
    const utilityValueRaw = watchedUtility?.value;
    const utilityValue =
      typeof utilityValueRaw === "object" && utilityValueRaw !== null
        ? utilityValueRaw?.value || utilityValueRaw?.id || null
        : utilityValueRaw;
    const isUtilityAll =
      !utilityValue || utilityValue === "All" || utilityValue === null;
    const bothAll = isStateAll && isUtilityAll;

    // Case 1: State is "All" → Load utilities from market
    // Called when: User changes state to "All" (after initial load)
    // API: GET /utilities/market/{marketIdentifier}
    // Always reload when state is "All" (user action) or if it's initial load
    if (isStateAll && marketIdentifier) {
      // When state is "All", set rate class to "None" immediately
      if (!isTexas) {
        setRateClassToNone();
      }
      // Always reload utilities from market when state is "All"
      // If it's initial load, always reload
      // If not initial load, reload if:
      //   - State changed (including undefined -> "All" transition)
      //   - OR last fetch was from state API (meaning user had a specific state before)
      //   - OR last fetch mode is undefined (first time after initial load)
      // This ensures utilities reload even if state was already "All" before initial load
      const last = lastStateUtilitiesFetchRef.current;
      const shouldReload =
        isInitialLoadRef.current ||
        (!isInitialLoadRef.current &&
          (stateChanged ||
            stateChangedToAll ||
            last.mode === undefined ||
            last.mode === "state" ||
            (last.mode === "market" && last.market !== marketIdentifier)));

      if (shouldReload) {
        lastStateUtilitiesFetchRef.current = {
          mode: "market",
          market: marketIdentifier,
          state: "All",
        };
        const commodityLabel =
          selectedMarket?.commodity &&
          selectedMarket.commodity.toLowerCase().includes("gas")
            ? "NatGas"
            : "Electric";
        setDropdownLoadingState("utilities", true);
        fetchUtilitiesByMarketApi({
          marketIdentifier,
          commodity: commodityLabel,
          userToken: accessToken,
        })
          .then((list) => {
            setAvailableUtilities(list);
            // When state is "All", set rate class to "None" since we can't fetch rate classes without a specific state
            if (!isTexas) {
              setRateClassToNone();
            }
          })
          .catch((err) => console.error("Error loading utilities", err))
          .finally(() => setDropdownLoadingState("utilities", false));
      } else {
        // Even if utilities aren't reloaded, set rate class to "None" when state is "All"
        if (!isTexas) {
          setRateClassToNone();
        }
      }
    }
    // Case 2: Specific state selected → Load utilities based on state
    // Called when: User selects a specific state (not "All")
    // API: POST /utility/bystate with state identifier
    // Only reload if state actually changed to preserve utilities when state hasn't changed
    else if (!isStateAll && currentStateValue && currentStateValue !== "All") {
      // debugger;
      if (stateChanged) {
        preventingCycleRef.current = false;
        const last = lastStateUtilitiesFetchRef.current;
        if (last.mode !== "state" || last.state !== currentStateValue) {
          lastStateUtilitiesFetchRef.current = {
            mode: "state",
            state: currentStateValue,
            market: marketIdentifier,
          };
          fetchUtilitiesForState(currentStateValue).then(() => {
            // After utilities are fetched, check if utility is "All" or not selected
            // If so, set rate class to "None" since we can't fetch rate classes without a utility
            const utilityValueRaw = watchedUtility?.value;
            const utilityValue =
              typeof utilityValueRaw === "object" && utilityValueRaw !== null
                ? utilityValueRaw?.value || utilityValueRaw?.id || null
                : utilityValueRaw;
            const normalizedUtilityValue =
              !utilityValue || utilityValue === "All" || utilityValue === null
                ? "All"
                : utilityValue;

            if (normalizedUtilityValue === "All" && !isTexas) {
              // No utility selected, set rate class to "None"
              setRateClassToNone();
            }
          });
        }
      }
      // If state didn't change, preserve existing utilities list
    } else {
      lastStateUtilitiesFetchRef.current = { mode: undefined };
      setAvailableUtilities([]);
      setDropdownLoadingState("utilities", false);
    }

    if (!isGasMarket && !isStateAll && currentStateValue) {
      // Only fetch load zones when not gas market, state is Texas, and a specific state is selected (not "All")
      setDropdownLoadingState("loadZones", true);
      fetchLoadZonesApi({
        stateIdentifier: currentStateValue,
        userToken: accessToken,
      })
        .then(setAvailableLoadZones)
        .catch(console.error)
        .finally(() => setDropdownLoadingState("loadZones", false));
    } else {
      setAvailableLoadZones([]);
      setDropdownLoadingState("loadZones", false);
    }
  }, [
    watchedState,
    watchedUtility,
    watchedCommodity,
    selectedMarket,
    accessToken,
    setDropdownLoadingState,
    fetchUtilitiesForState,
    fetchUtilitiesByMarketApi,
    fetchLoadZonesApi,
    fetchRateClassesForUtility,
    dispatch,
    setValue,
    setRateClassToNone,
  ]);

  /**
   * Helper function to call rate class API after state API completes
   * This ensures rate class API is only called after state processing is done
   */
  const callRateClassApiAfterStateComplete = useCallback(() => {
    // debugger;
    if (!accessToken) {
      setAvailableRateClasses([]);
      setDropdownLoadingState("rateClasses", false);
      return;
    }

    const utilityValueRaw = watchedUtility?.value;
    const utilityValue =
      typeof utilityValueRaw === "object" && utilityValueRaw !== null
        ? utilityValueRaw?.value || utilityValueRaw?.id || null
        : utilityValueRaw;

    const normalizedUtilityValue =
      !utilityValue || utilityValue === "All" || utilityValue === null
        ? "All"
        : utilityValue;
    const hasUtilitySelection = normalizedUtilityValue !== "All";

    // Use getValues to get current form value instead of watchedState (which may be stale)
    const currentStateFromForm = getValues("stateCode");
    const isTexas = currentStateFromForm?.name === "Texas";

    if (!hasUtilitySelection) {
      // When utility is "All", set rate class to "None"
      if (!isTexas) {
        setRateClassToNone();
      }
      // debugger;
      setAvailableRateClasses([]);
      setDropdownLoadingState("rateClasses", false);
      return;
    }
    if (isTexas) {
      // debugger;
      setAvailableRateClasses([]);
      setDropdownLoadingState("rateClasses", false);
      return;
    }

    const commodity =
      selectedMarket?.commodity === "Electricity" ||
      selectedMarket?.commodity === "Electric"
        ? 1
        : 2;

    fetchRateClassesForUtility(normalizedUtilityValue, commodity);
  }, [
    accessToken,
    watchedUtility,
    selectedMarket,
    fetchRateClassesForUtility,
    setDropdownLoadingState,
    getValues,
    setRateClassToNone,
  ]);

  /**
   * Sync watchedState to Redux selectedState
   * This ensures the conditional rendering in AccordianFilters uses the correct state
   */
  useEffect(() => {
    // Skip during auto-selection to prevent interference
    if (isAutoSelectingRef.current) {
      return;
    }

    if (!watchedState) {
      // If watchedState is null/undefined, set Redux to null
      if (pricingState.selectedState !== null) {
        dispatch(setSelectedState(null));
      }
      return;
    }

    // Extract state value and name
    const stateValueRaw = watchedState?.value;
    const stateValue =
      typeof stateValueRaw === "object" && stateValueRaw !== null
        ? stateValueRaw?.value || stateValueRaw?.id || null
        : stateValueRaw;
    const stateName = watchedState?.name;

    // Normalize "All" values
    const normalizedStateValue =
      !stateValue || stateValue === "All" || stateValue === null
        ? "All"
        : stateValue;
    const normalizedStateName =
      !stateName || stateName === "All" || stateName === null
        ? "All"
        : stateName;

    // Check if Redux state needs to be updated
    const currentReduxState = pricingState.selectedState;
    const needsUpdate =
      !currentReduxState ||
      currentReduxState.value !== normalizedStateValue ||
      currentReduxState.name !== normalizedStateName;

    if (needsUpdate) {
      if (normalizedStateValue === "All" || normalizedStateName === "All") {
        dispatch(setSelectedState(null));
      } else {
        dispatch(
          setSelectedState({
            value: normalizedStateValue,
            name: normalizedStateName,
          })
        );
      }
    }
  }, [watchedState, pricingState.selectedState, dispatch]);

  /**
   * Utility Watcher Effect
   *
   * Purpose: Handles utility dropdown changes and updates state dropdown accordingly.
   * Also handles Rate Class/Load Profile fetching based on utility and state.
   *
   * When called:
   * - On initial load (but skipped if isInitialLoadRef is true)
   * - When user selects a specific utility
   * - When user changes utility to "All"
   *
   * Behavior:
   * 1. If specific utility selected → Load states from utility API (POST /states/byutility)
   * 2. If utility changes to "All" and state is specific → Reload utilities from market API
   * 3. If utility changes to "All" (user action) → Reload states from market API (GET /states/market)
   * 4. Prevents cyclic API calls when both are "All"
   */
  useEffect(() => {
    // Extract utility value (handle both string and object formats)
    const currentUtilityValueRaw = watchedUtility?.value;
    const currentUtilityValue =
      typeof currentUtilityValueRaw === "object" &&
      currentUtilityValueRaw !== null
        ? currentUtilityValueRaw?.value || currentUtilityValueRaw?.id || null
        : currentUtilityValueRaw;

    // Extract state value (handle both string and object formats)
    const currentStateValueRaw = watchedState?.value;
    const currentStateValue =
      typeof currentStateValueRaw === "object" && currentStateValueRaw !== null
        ? currentStateValueRaw?.value || currentStateValueRaw?.id || null
        : currentStateValueRaw;

    // Normalize values for comparison
    const normalizedUtilityValue =
      !currentUtilityValue ||
      currentUtilityValue === "All" ||
      currentUtilityValue === null
        ? "All"
        : currentUtilityValue;
    const normalizedStateValue =
      !currentStateValue ||
      currentStateValue === "All" ||
      currentStateValue === null
        ? "All"
        : currentStateValue;

    const hasUtilitySelection = normalizedUtilityValue !== "All";
    const isStateAll = normalizedStateValue === "All";
    const isUtilityAll = normalizedUtilityValue === "All";
    const bothAll = isStateAll && isUtilityAll;
    // Use getValues to get current form value instead of watchedState (which may be stale)
    const currentStateFromForm = getValues("stateCode");
    const isTexas = currentStateFromForm?.name === "Texas";
    const isErcotMarket =
      selectedMarket?.name === "ERCOT" ||
      selectedMarket?.name?.toLowerCase() === "ercot";
    // Get default state for the selected market from constant mapping
    const defaultStateName = getDefaultStateForMarket(selectedMarket?.name);
    const isMarketWithDefaultState = defaultStateName !== null;
    const utilityChanged =
      prevUtilityValueRef.current !== normalizedUtilityValue;
    const texasChanged = prevIsTexasRef.current !== isTexas;
    prevUtilityValueRef.current = normalizedUtilityValue;
    prevIsTexasRef.current = isTexas;

    // Prevent cycles: if we're currently preventing cycles and both are "All", skip
    if (bothAll && preventingCycleRef.current) {
      lastUtilityStateFetchRef.current = {
        utility: normalizedUtilityValue,
        state: normalizedStateValue,
      };
      return;
    }
    // debugger;

    // Handle Load Profile reset for Texas
    if (isTexas) {
      if (texasChanged) {
        setValue("loadProfile", { id: "All", name: "All", value: "All" });
        dispatch(setSelectedLoadProfile(null));
      }
      setAvailableRateClasses([]);
      setDropdownLoadingState("rateClasses", false);
    } else if (texasChanged) {
      // Switched away from Texas; ensure load profile reset once
      dispatch(setSelectedLoadProfile(null));
      setValue("loadProfile", { id: "All", name: "All", value: "All" });
    }

    // Rate class API is now called only after state API completes (in callRateClassApiAfterStateComplete)
    // This ensures state API is called first, then rate class API

    // Case 1: Specific utility selected → Load states based on utility
    // Called when: User selects a specific utility (not "All")
    // API: POST /states/byutility with utility identifier
    // After loading states, check if Texas is selected, if yes load utilities from state API
    if (hasUtilitySelection && accessToken) {
      preventingCycleRef.current = false;
      const last = lastUtilityStateFetchRef.current;
      if (
        last?.utility !== normalizedUtilityValue ||
        last?.state !== normalizedStateValue
      ) {
        lastUtilityStateFetchRef.current = {
          utility: normalizedUtilityValue,
          state: normalizedStateValue,
        };

        // Load states from utility API first
        fetchStatesForUtilitySelection(currentUtilityValue, currentStateValue)
          .then((statesList) => {
            // After states are loaded, check if ERCOT+Texas logic applies
            let stateApiPromise: Promise<any> = Promise.resolve();

            if (isMarketWithDefaultState && defaultStateName && statesList) {
              const texasState = statesList.find(
                (s: any) => s?.name === defaultStateName
              );
              // Use getValues to get current form value instead of watchedState (which may be stale)
              const currentStateFromForm = getValues("stateCode");
              const isDefaultStateSelected =
                currentStateFromForm?.name === defaultStateName;

              if (texasState && !isDefaultStateSelected) {
                // Auto-select Texas if not already selected
                isAutoSelectingRef.current = true;
                dispatch(
                  setSelectedState({
                    value: texasState.value,
                    name: texasState.name,
                  })
                );
                setValue("stateCode", {
                  id: texasState.value,
                  name: texasState.name,
                  value: texasState.value,
                });

                // Load utilities from state API (Texas)
                stateApiPromise = fetchUtilitiesForState(texasState.value);
                isAutoSelectingRef.current = false;
              } else if (isTexas && currentStateValue) {
                // Texas is already selected, load utilities from state API
                stateApiPromise = fetchUtilitiesForState(currentStateValue);
              }
            } else if (isTexas && currentStateValue) {
              // Texas is selected (not ERCOT), load utilities from state API
              stateApiPromise = fetchUtilitiesForState(currentStateValue);
            }

            // After state API completes, call rate class API
            stateApiPromise
              .then(() => {
                callRateClassApiAfterStateComplete();
              })
              .catch((error) => {
                console.error(
                  "Error in state API, still calling rate class API",
                  error
                );
                callRateClassApiAfterStateComplete();
              });
          })
          .catch((error) => {
            console.error("Error loading states for utility", error);
            // Even on error, try to call rate class API
            callRateClassApiAfterStateComplete();
          });
      } else {
        // Utility/state combination hasn't changed, but utility changed, so call rate class API
        if (utilityChanged) {
          callRateClassApiAfterStateComplete();
        }
      }
    }
    // Case 2: Utility changed to "All" (user action) → Reload states from market
    // Called when: User changes utility to "All" (regardless of state value)
    // API: GET /states/market/{marketIdentifier} - Reload states from market
    // After loading states, check ERCOT+Texas, if yes auto-select Texas and load utilities from state API
    // If not Texas, load utilities from market API
    else if (
      isUtilityAll &&
      utilityChanged &&
      accessToken &&
      selectedMarket?.name &&
      !isInitialLoadRef.current
    ) {
      const marketIdentifier = selectedMarket?.value;
      if (marketIdentifier && !preventingCycleRef.current) {
        preventingCycleRef.current = true;
        lastUtilityStateFetchRef.current = {
          utility: normalizedUtilityValue,
          state: normalizedStateValue,
        };
        lastStateUtilitiesFetchRef.current = {
          mode: "market",
          market: marketIdentifier,
          state: normalizedStateValue,
        };

        // Get default state for the selected market from constant mapping
        const utilityDefaultStateName = getDefaultStateForMarket(
          selectedMarket?.name
        );
        const utilityIsMarketWithDefaultState =
          utilityDefaultStateName !== null;
        // Reload states from market
        fetchStatesForMarket()
          .then(({ list }) => {
            setAvailableStates(list);
            dispatch(setStates(list));

            // Check if ERCOT+Texas logic applies
            if (
              utilityIsMarketWithDefaultState &&
              utilityDefaultStateName &&
              list
            ) {
              const texasState = list.find(
                (s: any) => s?.name === utilityDefaultStateName
              );
              if (texasState) {
                // Auto-select Texas
                isAutoSelectingRef.current = true;
                dispatch(
                  setSelectedState({
                    value: texasState.value,
                    name: texasState.name,
                  })
                );
                setValue("stateCode", {
                  id: texasState.value,
                  name: texasState.name,
                  value: texasState.value,
                });

                // Load utilities from state API (Texas)
                fetchUtilitiesForState(texasState.value);
                isAutoSelectingRef.current = false;
              } else {
                // No Texas, reset state to "All" and load utilities from market
                dispatch(setSelectedState(null));
                setValue("stateCode", { id: "All", name: "All", value: "All" });

                const commodityLabel =
                  selectedMarket?.commodity &&
                  selectedMarket.commodity.toLowerCase().includes("gas")
                    ? "NatGas"
                    : "Electric";
                setDropdownLoadingState("utilities", true);
                fetchUtilitiesByMarketApi({
                  marketIdentifier,
                  commodity: commodityLabel,
                  userToken: accessToken,
                })
                  .then((utilitiesList) => {
                    setAvailableUtilities(utilitiesList);
                  })
                  .catch((err) => {
                    console.error("Error loading utilities for market", err);
                  })
                  .finally(() => setDropdownLoadingState("utilities", false));
              }
            } else {
              // Not ERCOT, reset state to "All" and load utilities from market
              if (isStateAll) {
                dispatch(setSelectedState(null));
                setValue("stateCode", { id: "All", name: "All", value: "All" });
              }

              const commodityLabel =
                selectedMarket?.commodity &&
                selectedMarket.commodity.toLowerCase().includes("gas")
                  ? "NatGas"
                  : "Electric";
              setDropdownLoadingState("utilities", true);
              fetchUtilitiesByMarketApi({
                marketIdentifier,
                commodity: commodityLabel,
                userToken: accessToken,
              })
                .then((utilitiesList) => {
                  setAvailableUtilities(utilitiesList);
                })
                .catch((err) => {
                  console.error("Error loading utilities for market", err);
                })
                .finally(() => setDropdownLoadingState("utilities", false));
            }
          })
          .catch((err) => {
            console.error("Error loading states by market", err);
          });
      }
      lastUtilityStateFetchRef.current = {
        utility: undefined,
        state: undefined,
      };
    }
  }, [
    watchedUtility,
    watchedState,
    selectedMarket,
    accessToken,
    fetchStatesForUtilitySelection,
    fetchUtilitiesForState,
    fetchStatesForMarket,
    fetchUtilitiesByMarketApi,
    setRateClassToNone,
    callRateClassApiAfterStateComplete,
    getValues,
    setValue,
    dispatch,
    setDropdownLoadingState,
  ]);

  // // When Utility changes, fetch rate classes
  // useEffect(() => {
  //   if (!isFilterDrawerOpen || !watchedRateClass) return;
  //   dispatch(fetchSuppliersApi(watchedRateClass.value));
  //   // or dispatch({ type: FETCH_SUPPLIERS, payload: watchedRateClass.value });
  // }, [watchedRateClass, isFilterDrawerOpen, dispatch]);

  // Initial form setup - only run once when terms are loaded
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    // debugger;
    if (
      !pricingState?.terms ||
      pricingState.terms.length === 0 ||
      hasInitialized
    )
      return;

    const currentTerm = pricingState.terms.filter((x) => x.isSelected)[0];
    const currentTermValue = currentTerm?.value ?? "";
    const currentTermName = currentTerm?.name ?? "All";

    const startIso =
      pricingState.startToDate || pricingState.startFromDate || null;
    const startDateSelection =
      parseIsoToDate(startIso) || getNextMonthStartDate();
    const effectiveIso =
      pricingState.effectiveToDate || pricingState.effectiveFromDate || null;
    const effectiveDateSelection =
      pricingState.effectiveFromDate && pricingState.effectiveToDate
        ? [
            parseIsoToDate(pricingState.effectiveFromDate),
            parseIsoToDate(pricingState.effectiveToDate),
          ]
        : effectiveIso
        ? parseIsoToDate(effectiveIso)
        : getDealBookedDateRange();

    const filtersInState = {
      commodity: String(pricingState.commodity || 1),
      stateCode: pricingState.selectedState
        ? {
            id: pricingState.selectedState.value,
            name: pricingState.selectedState.name,
            value: pricingState.selectedState.value,
          }
        : { id: "All", name: "All", value: "All" },
      term: {
        id: currentTermValue,
        name: currentTermName,
        value: currentTermValue,
      },
      // productType: {id: currentTermValue, name: currentTermName , value: currentTermValue},
      fromDate: "",
      toDate: "",
      effectiveDate: effectiveDateSelection,
      startToDate: startDateSelection,
      noOfMonth: pricingState.noOfMonths
        ? {
            id: pricingState.noOfMonths,
            name: pricingState.noOfMonths,
            value: pricingState.noOfMonths,
          }
        : { id: "1", name: "1", value: "1" },
      utility: pricingState.selectedUtility
        ? {
            id: pricingState.selectedUtility.value,
            name: pricingState.selectedUtility.name,
            value: pricingState.selectedUtility.value,
          }
        : { id: "All", name: "All", value: "All" },
      rateClass: pricingState.selectedRateClass
        ? {
            id: pricingState.selectedRateClass.value,
            value: pricingState.selectedRateClass.value,
            name: pricingState.selectedRateClass.name,
          }
        : { id: "None", value: "None", name: "None" },
      loadZone: pricingState.selectedLoadZone
        ? {
            id: pricingState.selectedLoadZone.value,
            value: pricingState.selectedLoadZone.value,
            name: pricingState.selectedLoadZone.name,
          }
        : { id: "All", value: "All", name: "All" },
      billingMethod: pricingState.selectedBillingMethod
        ? {
            id: pricingState.selectedBillingMethod.value,
            value: pricingState.selectedBillingMethod.value,
            name: pricingState.selectedBillingMethod.name,
          }
        : { id: "All", value: "All", name: "All" },
      productType:
        pricingState.productTypes
          ?.filter((pt: any) => pt.isSelected)
          ?.map((pt: any) => ({ name: pt.name, value: pt.value })) || [],
      // Pass Through:
      // If there are selected cost components in Redux, use them.
      // Otherwise, default to "All" so it is prefilled and badge is visible.
      passThrough: (() => {
        const selectedPassThrough =
          pricingState.costComponents
            ?.filter((cc: any) => cc.isSelected)
            ?.map((cc: any) => ({ name: cc.name, value: cc.value })) || [];

        if (selectedPassThrough.length > 0) {
          return selectedPassThrough;
        }

        // Prefill default "All" option to match defaultValues shape
        return [{ id: "All", name: "All", value: "All" }];
      })(),
      customerType: pricingState.customerClass
        ? {
            id: pricingState.customerClass,
            name: pricingState.customerClass,
            value: pricingState.customerClass,
          }
        : { id: "All", name: "All", value: "All" },
      usage: pricingState.usage ?? null,
      usageFrom: pricingState.usageFrom ?? null,
      usageTo: pricingState.usageTo ?? null,
      demand: pricingState.demand ?? null,
      loadFactor: pricingState.loadFactor ?? null,
      loadFactorFrom: pricingState.loadFactorFrom ?? null,
      loadFactorTo: pricingState.loadFactorTo ?? null,
    };
    if (hasInitializedFiltersRef.current) {
      return;
    }
    isResetting.current = true;
    reset(filtersInState);
    hasInitializedFiltersRef.current = true;
    setHasInitialized(true);
  }, [
    pricingState.selectedState,
    pricingState.selectedUtility,
    pricingState.selectedRateClass,
    pricingState.selectedLoadZone,
    pricingState.selectedBillingMethod,
    pricingState.terms,
    pricingState.effectiveFromDate,
    pricingState.effectiveToDate,
    pricingState.productTypes,
    pricingState.costComponents,
    hasInitialized,
    reset,
  ]);

  // }, [isFilterDrawerOpen]);

  // // Reset dependant dropdowns when state changes
  // useEffect(() => {
  //   setAvailableRateClasses([{name: 'All', value: 'All', isSelected: true}]);
  //   dispatch(setSelectedRateClass(null));
  // }, [availableUtilities]);

  const onSubmit: SubmitHandler<any> = async (data) => {
    if (!accessToken) {
      console.warn("Access token not ready; skipping chart fetch");
      return;
    }
    if (isPremium !== true) {
      applyNonPremiumStaticCharts();
      return;
    }
    // setFilters(data);
    // closeFilterDrawer();

    const {
      commodity,
      stateCode,
      term,
      fromDate,
      toDate,
      effectiveDate,
      startToDate,
      noOfMonth,
      utility,
      rateClass,
      loadProfile,
      usage,
      usageFrom,
      usageTo,
      demand,
      loadFactor,
      loadFactorFrom,
      loadFactorTo,
      productType,
      passThrough,
      customerType,
    } = data;
    const chartKeys = [
      "priceTrend",
      "priceTrendMinMax",
      "forwardCurve",
      "forwardCurveMinMax",
    ];
    // Set isLoading true for all chart keys
    chartKeys.forEach((key) => {
      // dispatch(setChartsDataLoader(true, key));
      dispatch(setChartsData(null, key)); // Optional: clear old data
    });

    const selectedState =
      stateCode?.value && stateCode?.value !== "All" ? stateCode.value : "";
    const selectedUtilities =
      utility?.value && utility?.value !== "All" ? utility.value : "";
    const selectedTerm =
      term !== null && term !== undefined && term !== "" ? String(term) : "";
    const selectedProductType = productType?.map((pt) => pt?.value) || [];
    const selectedPassThroughRaw =
      passThrough?.map((item: any) => item?.value ?? item) || [];
    const displayPassThrough = selectedPassThroughRaw.filter(
      (value) => value && value !== "All"
    );
    //  productType?.value && productType?.value !== " " ? productType.value : '';
    dispatch(setCommodity(commodity || 1));
    dispatch(setSelectedState(data.stateCode ?? null));
    // dispatch(setSelectedUtilities(selectedUtilities || ''));
    dispatch(setSelectedUtility(data.utility ?? null));
    const normalizedNoOfMonths =
      noOfMonth?.value && noOfMonth.value !== "All" ? noOfMonth.value : "1";
    dispatch(setNoOfMonths(normalizedNoOfMonths));

    // Handle rate class vs load profile based on state
    const isTexas = data.stateCode?.name === "Texas";
    if (isTexas) {
      dispatch(setSelectedLoadProfile(data.loadProfile ?? null));
      // debugger;
      dispatch(setSelectedRateClass(null)); // Clear rate class for Texas
    } else {
      // debugger;
      dispatch(setSelectedRateClass(data.rateClass ?? null));
      dispatch(setSelectedLoadProfile(null)); // Clear load profile for non-Texas states
    }

    dispatch(setSelectedLoadZone(data.loadZone ?? null));
    dispatch(setSelectedBillingMethod(data.billingMethod ?? null));
    // numeric filters
    const toBoundedInt = (v: any, min: number, max: number) => {
      if (v === "" || v === undefined || v === null) return null;
      const n = Number(v);
      if (!Number.isFinite(n)) return null;
      const i = Math.floor(n);
      if (i < min) return min;
      if (i > max) return max;
      return i;
    };
    const sanitizeInt = (v: any, min: number, max: number) => {
      if (v === "" || v === undefined || v === null) return null;
      const n = Number(v);
      if (!Number.isFinite(n)) return null;
      return toBoundedInt(n, min, max);
    };
    // Always derive from current values to avoid stale dirty flags causing 0s
    dispatch(setUsage(sanitizeInt(usage, 0, 2000000)));
    dispatch(setUsageFrom(sanitizeInt(usageFrom, 0, 2000000)));
    dispatch(setUsageTo(sanitizeInt(usageTo, 0, 2000000)));
    dispatch(setDemand(sanitizeInt(demand, 0, 100000)));
    dispatch(setLoadFactor(sanitizeInt(loadFactor, 0, 2000000)));
    dispatch(setLoadFactorFrom(sanitizeInt(loadFactorFrom, 0, 2000000)));
    dispatch(setLoadFactorTo(sanitizeInt(loadFactorTo, 0, 2000000)));
    dispatch(setSelectedTerm(selectedTerm || ""));
    //product type
    // debugger;
    const normalizedProductTypes =
      selectedProductType && selectedProductType.length
        ? selectedProductType
        : [];
    dispatch(setSelectedProductType(normalizedProductTypes));
    //cost component
    dispatch(setSelectedCostComponent(displayPassThrough));
    // Update ref to preserve selections when commodity changes (but not when market changes)
    selectedCostComponentsRef.current = displayPassThrough;
    //customer class
    const selectedCustomerClass =
      customerType?.value && customerType?.value !== "All"
        ? customerType.value
        : "";
    dispatch(setCustomerClass(selectedCustomerClass));
    dispatch(setFromDate(toIsoDate(fromDate) || null));
    dispatch(setToDate(toIsoDate(toDate) || null));

    const convertDateObjectToIso = (dObj: any | null | undefined) => {
      if (!dObj) return null;
      try {
        // react-multi-date-picker DateObject has toDate() and year/month/day
        if (typeof dObj.toDate === "function") {
          return dObj.toDate().toISOString();
        }
        if (dObj instanceof Date) {
          return toIsoStringFromDate(dObj);
        }
        // Fallback if we get a string
        return toIsoDate(dObj) || null;
      } catch {
        return null;
      }
    };

    // Handle Effective Date range (start and end dates)
    if (Array.isArray(effectiveDate) && effectiveDate.length >= 2) {
      dispatch(setEffectiveFromDate(convertDateObjectToIso(effectiveDate[0])));
      dispatch(setEffectiveToDate(convertDateObjectToIso(effectiveDate[1])));
    } else if (effectiveDate) {
      const singleIso = convertDateObjectToIso(effectiveDate[0]);
      dispatch(setEffectiveFromDate(singleIso));
      dispatch(setEffectiveToDate(null));
    } else {
      dispatch(setEffectiveFromDate(null));
      dispatch(setEffectiveToDate(null));
    }

    if (startToDate) {
      const startIso = convertMonthSelectionToIso(startToDate);
      dispatch(setStartFromDate(null));
      dispatch(setStartToDate(startIso));
    } else {
      // If Start date is cleared, set to null (don't use default)
      dispatch(setStartFromDate(null));
      dispatch(setStartToDate(null));
    }

    const selectedClientIdentifier = getSelectedClientIdentifier();
    const showAll = isSystemAdmin ? Boolean(getValues("showAll")) : false;
    const valuesss = Boolean(getValues("showAll"));

    dispatch(
      fetchChartsData(
        clientId,
        selectedClientIdentifier,
        accessToken,
        showAll,
        isSystemAdmin,
        isPremium
      )
    );
    // await extra?.onApply(data);
  };

  // --- Saved Filters Handlers ---
  const fetchSavedFilters = useCallback(async () => {
    try {
      const response = await loadUserFiltersApi({ pageKey: "Pricing", userToken: accessToken });
      if (response?.success && Array.isArray(response.data)) {
        const parsed = response.data.map((item: any) => ({
          id: item.rowKey,
          name: item.name,
          filters: typeof item.filters === "string" ? JSON.parse(item.filters) : item.filters,
        }));
        setSavedFiltersList(parsed);
        return parsed;
      }
    } catch (error) {
      console.error("Error loading saved filters:", error);
    }
    return [];
  }, [accessToken]);

  const handleSaveFilters = useCallback(async (filterName: string) => {
    setSavingFilters(true);
    try {
      const formValues = getValues();
      const serialized: any = {
        commodity: formValues.commodity,
        stateCode: formValues.stateCode,
        toDate: formValues.toDate || "",
        fromDate: formValues.fromDate || null,
        effectiveDate: Array.isArray(formValues.effectiveDate)
          ? formValues.effectiveDate.map((d: any) => d instanceof Date ? dateToString(d) : d)
          : formValues.effectiveDate instanceof Date
            ? dateToString(formValues.effectiveDate)
            : formValues.effectiveDate || null,
        startToDate: formValues.startToDate instanceof Date
          ? dateToString(formValues.startToDate)
          : formValues.startToDate || null,
        noOfMonth: formValues.noOfMonth,
        term: formValues.term,
        utility: formValues.utility,
        rateClass: formValues.rateClass,
        loadProfile: formValues.loadProfile,
        loadZone: formValues.loadZone,
        billingMethod: formValues.billingMethod,
        usage: formValues.usage,
        usageFrom: formValues.usageFrom,
        usageTo: formValues.usageTo,
        demand: formValues.demand,
        loadFactor: formValues.loadFactor,
        loadFactorFrom: formValues.loadFactorFrom ?? null,
        loadFactorTo: formValues.loadFactorTo ?? null,
        productType: formValues.productType,
        passThrough: formValues.passThrough,
        customerType: formValues.customerType,
        showAll: formValues.showAll,
        clientOverride: formValues.clientOverride,
      };
      const response = await saveUserFiltersApi({ pageKey: "Pricing", name: filterName, filters: serialized, userToken: accessToken });
      if (response?.success) {
        snackbar(response.message || "Filter saved successfully", "success");
        const updatedFilters = await fetchSavedFilters();
        if (response.rowKey) {
          setSelectedSavedFilterId(response.rowKey);
          setTimeout(() => {
            applySavedFilter(response.rowKey, updatedFilters);
          }, 100);
        }
      } else {
        snackbar(response?.message || "Failed to save filter", "error");
      }
    } catch (error: any) {
      console.error("Error saving filters:", error);
      snackbar(error?.message || "An error occurred while saving filter", "error");
    } finally {
      setSavingFilters(false);
    }
  }, [getValues, snackbar, fetchSavedFilters, accessToken]);

  const handleDeleteSavedFilter = useCallback(async (filterId: string) => {
    try {
      const response = await deleteUserFilterApi({ filterId, pageKey: "Pricing", userToken: accessToken });
      if (response?.success) {
        snackbar(response.message || "Filter deleted successfully", "success");
        if (selectedSavedFilterId === filterId) {
          setSelectedSavedFilterId(null);
        }
        await fetchSavedFilters();
      } else {
        snackbar(response?.message || "Failed to delete filter", "error");
      }
    } catch (error: any) {
      console.error("Error deleting saved filter:", error);
      snackbar(error?.message || "An error occurred while deleting filter", "error");
    }
  }, [snackbar, fetchSavedFilters, selectedSavedFilterId, accessToken]);

  const applySavedFilter = useCallback((filterId: string, filterList?: any[]) => {
    const list = filterList || savedFiltersList;
    const savedFilter = list.find((f: any) => f.id === filterId);
    if (!savedFilter || !savedFilter.filters) return;
    setSelectedSavedFilterId(filterId);
    const f = savedFilter.filters;

    // Parse effectiveDate (date range for custom pricing)
    let effectiveDateValue: any = getDealBookedDateRange();
    if (Array.isArray(f.effectiveDate)) {
      effectiveDateValue = f.effectiveDate.map((d: string) => new Date(d));
    } else if (f.effectiveDate) {
      effectiveDateValue = new Date(f.effectiveDate);
    }

    // Apply to form
    reset({
      commodity: f.commodity ?? "1",
      stateCode: f.stateCode ?? { id: "All", name: "All" },
      toDate: f.toDate ?? "",
      fromDate: f.fromDate ?? null,
      effectiveDate: effectiveDateValue,
      startToDate: f.startToDate ? new Date(f.startToDate) : getNextMonthStartDate(),
      noOfMonth: f.noOfMonth ?? { id: "12", name: "12", value: "12" },
      term: f.term ?? null,
      utility: f.utility ?? { id: "All", name: "All", value: "All" },
      rateClass: f.rateClass ?? { id: "None", value: "None", name: "None" },
      loadProfile: f.loadProfile ?? { id: "All", value: "All", name: "All" },
      loadZone: f.loadZone ?? { id: "All", value: "All", name: "All" },
      billingMethod: f.billingMethod ?? { id: "All", value: "All", name: "All" },
      usage: f.usage ?? null,
      usageFrom: f.usageFrom ?? null,
      usageTo: f.usageTo ?? null,
      demand: f.demand ?? null,
      loadFactor: f.loadFactor ?? null,
      loadFactorFrom: f.loadFactorFrom ?? null,
      loadFactorTo: f.loadFactorTo ?? null,
      productType: f.productType ?? getDefaultProductTypeSelection(),
      passThrough: f.passThrough ?? [{ id: "All", name: "All", value: "All" }],
      customerType: f.customerType ?? { id: "Commercial", name: "Commercial", value: "Commercial" },
      showAll: f.showAll ?? false,
      clientOverride: f.clientOverride ?? { id: "None", value: "None", name: "None" },
    });

    // Apply to Redux
    dispatch(setCommodity(Number(f.commodity) || 1));
    if (f.stateCode && f.stateCode.id !== "All") {
      dispatch(setSelectedState({ value: f.stateCode.id, name: f.stateCode.name }));
    } else {
      dispatch(setSelectedState(null));
    }
    dispatch(setSelectedTerm(f.term ?? null));
    if (f.utility && f.utility.id !== "All") {
      dispatch(setSelectedUtility({ value: f.utility.id || f.utility.value, name: f.utility.name }));
    } else {
      dispatch(setSelectedUtility(null));
    }
    if (f.rateClass && f.rateClass.id !== "None") {
      dispatch(setSelectedRateClass({ value: f.rateClass.id || f.rateClass.value, name: f.rateClass.name }));
    } else {
      dispatch(setSelectedRateClass(null));
    }
    if (f.loadProfile && f.loadProfile.id !== "All") {
      dispatch(setSelectedLoadProfile({ value: f.loadProfile.id || f.loadProfile.value, name: f.loadProfile.name }));
    } else {
      dispatch(setSelectedLoadProfile(null));
    }
    if (f.loadZone && f.loadZone.id !== "All") {
      dispatch(setSelectedLoadZone({ value: f.loadZone.id || f.loadZone.value, name: f.loadZone.name }));
    } else {
      dispatch(setSelectedLoadZone(null));
    }
    if (f.billingMethod && f.billingMethod.id !== "All") {
      dispatch(setSelectedBillingMethod({ value: f.billingMethod.id || f.billingMethod.value, name: f.billingMethod.name }));
    } else {
      dispatch(setSelectedBillingMethod(null));
    }
    dispatch(setUsage(f.usage ?? null));
    dispatch(setUsageFrom(f.usageFrom ?? null));
    dispatch(setUsageTo(f.usageTo ?? null));
    dispatch(setDemand(f.demand ?? null));
    dispatch(setLoadFactor(f.loadFactor ?? null));
    dispatch(setNoOfMonths(f.noOfMonth?.value ?? "12"));
    dispatch(setCustomerClass(f.customerType?.value ?? "Commercial"));
    if (f.productType) {
      dispatch(setSelectedProductType(f.productType));
    }
    if (f.passThrough) {
      dispatch(setSelectedCostComponent(f.passThrough.map((p: any) => p.value)));
    }
    if (f.startToDate) {
      dispatch(setStartToDate(toIsoStringFromDate(new Date(f.startToDate))));
    }
    if (Array.isArray(f.effectiveDate) && f.effectiveDate.length === 2) {
      dispatch(setEffectiveFromDate(toIsoStringFromDate(new Date(f.effectiveDate[0]))));
      dispatch(setEffectiveToDate(toIsoStringFromDate(new Date(f.effectiveDate[1]))));
    }

    setEffectiveResetKey((k) => k + 1);

    // Auto-submit the form after applying saved filter
    setTimeout(() => {
      handleSubmit(onSubmit)();
    }, 100);
  }, [savedFiltersList, reset, dispatch, setValue]);

  // Load saved filters on mount and auto-apply the first one
  useEffect(() => {
    if (savedFiltersLoadedRef.current) return;
    savedFiltersLoadedRef.current = true;
    const loadAndApply = async () => {
      await fetchSavedFilters();
    };
    loadAndApply();
  }, [fetchSavedFilters]);

  const resetFilters = () => {
    // Unselect any saved filter
    setSelectedSavedFilterId(null);
    // debugger;
    // Reset the auto-apply flag so we can reinitialize like page load
    hasAutoAppliedRef.current = false;
    hasInitializedFiltersRef.current = false;
    isInitialLoadRef.current = true;
    setIsInitialLoadComplete(false); // debugger;
    // Clear all Redux state
    dispatch(setCommodity(1));
    dispatch(setSelectedMarket(null));
    dispatch(setSelectedState(null));
    dispatch(setSelectedTerm(""));
    setValue("term", null);
    dispatch(setSelectedUtility(null));
    dispatch(setSelectedRateClass(null));
    dispatch(setSelectedLoadProfile(null));
    dispatch(setSelectedLoadZone(null));
    dispatch(setSelectedBillingMethod(null));
    dispatch(setUsage(null));
    dispatch(setUsageFrom(null));
    dispatch(setUsageTo(null));
    dispatch(setDemand(null));
    dispatch(setLoadFactor(null));
    dispatch(setLoadFactorFrom(null));
    dispatch(setLoadFactorTo(null));
    dispatch(setFromDate(null));
    dispatch(setToDate(null));
    // Set default range in Redux on reset: current - 60 days to current
    const [fromDate, toDate] = getDealBookedDateRange();
    const defaultEffectiveFromIso = toIsoStringFromDate(fromDate);
    const defaultEffectiveToIso = toIsoStringFromDate(toDate);
    dispatch(setEffectiveFromDate(defaultEffectiveFromIso));
    dispatch(setEffectiveToDate(defaultEffectiveToIso));
    // Mark as initialized since we've set the default dates
    hasInitializedEffectiveDateRef.current = true;
    // Set default start date in Redux on reset: current month's date
    const defaultStartIso = toIsoStringFromDate(getNextMonthStartDate());
    dispatch(setStartFromDate(null));
    dispatch(setStartToDate(defaultStartIso));
    // Mark as initialized since we've set the default start date
    hasInitializedStartDateRef.current = true;
    dispatch(setNoOfMonths("12"));
    dispatch(setCustomerClass("Commercial"));

    // Clear local options except states; we'll repopulate dependents below
    // Keep availableStates so we can reselect single-state markets immediately
    setAvailableUtilities([]);
    setAvailableRateClasses([]);
    dispatch(setSelectedProductType([DEFAULT_PRODUCT_TYPE_VALUE]));
    // Reset Pass Through to default "All" in Redux and local ref
    dispatch(setSelectedCostComponent(["All"]));
    selectedCostComponentsRef.current = ["All"];
    // Reset form to initial state
    reset({
      commodity: "1",
      stateCode: { id: "All", name: "All" },
      toDate: "",
      fromDate: null,
      effectiveDate: getDealBookedDateRange(),
      startToDate: getNextMonthStartDate(),
      noOfMonth: { id: "12", name: "12", value: "12" },
      term: null,
      utility: { id: "All", name: "All", value: "All" },
      rateClass: { id: "None", value: "None", name: "None" },
      loadProfile: { id: "All", value: "All", name: "All" },
      loadZone: { id: "All", value: "All", name: "All" },
      billingMethod: { id: "All", value: "All", name: "All" },
      usage: null,
      usageFrom: null,
      usageTo: null,
      demand: null,
      loadFactor: null,
      loadFactorFrom: null,
      loadFactorTo: null,
      productType: getDefaultProductTypeSelection(),
      // Reset Pass Through field to default "All"
      passThrough: [{ id: "All", name: "All", value: "All" }],
      customerType: {
        id: "Commercial",
        name: "Commercial",
        value: "Commercial",
      },
      showAll: false,
      clientOverride: { id: "None", value: "None", name: "None" },
    });
    // Explicitly set clientOverride to "None" after reset to ensure it's applied
    // Use setTimeout to ensure it runs after the reset completes
    setTimeout(() => {
      const noneOption = {
        id: "None",
        value: "None",
        name: "None",
        isSelected: false,
      };
      // Ensure "None" is in the adminClients list if it's not already there
      if (availableAdminClients.length > 0) {
        const hasNone = availableAdminClients.some(
          (client: any) => client.value === "None"
        );
        if (!hasNone) {
          setAvailableAdminClients([noneOption, ...availableAdminClients]);
        }
      } else if (isSystemAdmin) {
        // If adminClients is empty but user is super admin, add "None" option
        setAvailableAdminClients([noneOption]);
      }
      // Set the value using the exact option from the list if available, otherwise use the default structure
      if (setValue) {
        const existingNoneOption = availableAdminClients.find(
          (client: any) => client.value === "None"
        ) as any;
        const valueToSet = existingNoneOption
          ? {
              id: existingNoneOption.id || existingNoneOption.value,
              value: existingNoneOption.value,
              name: existingNoneOption.name,
            }
          : { id: "None", value: "None", name: "None" };
        setValue("clientOverride", valueToSet, { shouldValidate: false });
      }
    }, 0);
    setEffectiveResetKey((k) => k + 1);

    // Ensure market is set (ERCOT preferred) to rehydrate lists
    const defaultMarket =
      pricingState.markets?.find((m) => m.name === "ERCOT") ||
      pricingState.markets?.[0];
    if (defaultMarket) {
      dispatch(setSelectedMarket(defaultMarket as any));
    }

    // If current market has exactly one state, auto-select it and load dependents immediately
    const validStates = (availableStates || []).filter(
      (s) => s?.name && s.name !== "All"
    );
    if (accessToken && validStates.length === 1) {
      const singleState = validStates[0];
      dispatch(
        setSelectedState({ value: singleState.value, name: singleState.name })
      );
      setValue("stateCode", {
        id: singleState.value,
        name: singleState.name,
        value: singleState.value,
      });

      const marketCommodity =
        (defaultMarket as any)?.commodity ??
        (pricingState.selectedMarket as any)?.commodity;
      const commodity =
        marketCommodity === "Electricity" ||
        marketCommodity === "Electric" ||
        marketCommodity === 1 ||
        marketCommodity === "1"
          ? 1
          : 2;
      const isGasMarket =
        marketCommodity &&
        (String(marketCommodity).toLowerCase().includes("gas") ||
          String(marketCommodity).toLowerCase().includes("natgas"));

      fetchUtilitiesApi({
        state: singleState.name,
        commodity,
        userToken: accessToken,
      })
        .then((utilitiesList) => {
          setAvailableUtilities(utilitiesList);
          const validUtilities = utilitiesList.filter(
            (util) => util.name && util.name !== "All"
          );
          if (validUtilities.length === 1) {
            const singleUtility = validUtilities[0];
            dispatch(
              setSelectedUtility({
                value: singleUtility.value,
                name: singleUtility.name,
              })
            );
            setValue("utility", {
              id: singleUtility.value,
              name: singleUtility.name,
              value: singleUtility.value,
            });
          }
        })
        .catch(console.error);

      // Only fetch load zones for non-gas markets and when state is Texas
      if (!isGasMarket) {
        fetchLoadZonesApi({
          stateIdentifier: singleState.value,
          userToken: accessToken,
        })
          .then((loadZonesList) => {
            setAvailableLoadZones(loadZonesList);
            const validLoadZones = loadZonesList.filter(
              (lz) => lz.name && lz.name !== "All"
            );
            if (validLoadZones.length === 1) {
              const singleLoadZone = validLoadZones[0];
              dispatch(
                setSelectedLoadZone({
                  value: singleLoadZone.value,
                  name: singleLoadZone.name,
                })
              );
              setValue("loadZone", {
                id: singleLoadZone.value,
                name: singleLoadZone.name,
                value: singleLoadZone.value,
              });
            }
          })
          .catch(console.error);
      }
    }

    // Reset hasInitialized to trigger the initialization flow
    setHasInitialized(false);
  };

  const onDeleteFilter = (filterName: string) => {
    // debugger;
    switch (filterName) {
      case "Market": {
        dispatch(setSelectedMarket(null as any));
        break;
      }
      case "State": {
        dispatch(setSelectedState(null));
        dispatch(setSelectedUtility(null));
        dispatch(setSelectedRateClass(null));
        dispatch(setSelectedLoadProfile(null));
        dispatch(setSelectedLoadZone(null));
        setValue("stateCode", { id: "All", name: "All", value: "All" });
        setValue("utility", { id: "All", name: "All", value: "All" });
        setValue("rateClass", { id: "All", name: "All", value: "All" });
        setValue("loadProfile", { id: "All", name: "All", value: "All" });
        setValue("loadZone", { id: "All", name: "All", value: "All" });
        break;
      }
      case "Utility": {
        dispatch(setSelectedUtility(null));
        dispatch(setSelectedRateClass(null));
        setValue("utility", { id: "All", name: "All", value: "All" });
        setValue("rateClass", { id: "All", name: "All", value: "All" });
        break;
      }
      case "Load Zone": {
        dispatch(setSelectedLoadZone(null));
        setValue("loadZone", { id: "All", name: "All", value: "All" });
        break;
      }
      case "Rate Class": {
        dispatch(setSelectedRateClass(null));
        setValue("rateClass", { id: "All", name: "All", value: "All" });
        break;
      }
      case "Load Profile": {
        dispatch(setSelectedLoadProfile(null));
        setValue("loadProfile", { id: "All", name: "All", value: "All" });
        break;
      }
      case "Billing Method": {
        dispatch(setSelectedBillingMethod(null));
        setValue("billingMethod", { id: "All", name: "All", value: "All" });
        break;
      }
      case "Term": {
        dispatch(setSelectedTerm(""));
        setValue("term", null);
        break;
      }
      case "Booked Date": {
        // Revert to default range (current - 60 days to current) when badge is canceled
        const [fromDate, toDate] = getDealBookedDateRange();
        const defaultEffectiveFromIso = toIsoStringFromDate(fromDate);
        const defaultEffectiveToIso = toIsoStringFromDate(toDate);
        dispatch(setEffectiveFromDate(defaultEffectiveFromIso));
        dispatch(setEffectiveToDate(defaultEffectiveToIso));
        setValue("effectiveDate", [fromDate, toDate]);
        // Reset the ref so it can be initialized again if needed
        hasInitializedEffectiveDateRef.current = true;
        break;
      }
      case "Start Date": {
        // Revert to current month when badge is canceled
        const currentMonthStartIso = toIsoStringFromDate(
          getNextMonthStartDate()
        );
        dispatch(setStartFromDate(null));
        dispatch(setStartToDate(currentMonthStartIso));
        setValue("startToDate", getNextMonthStartDate());
        // Reset the ref so it can be initialized again if needed
        hasInitializedStartDateRef.current = true;
        break;
      }
      case "No Of Month": {
        dispatch(setNoOfMonths("12"));
        setValue("noOfMonth", { id: "12", name: "12", value: "12" });
        break;
      }
      case "Annual Usage": {
        dispatch(setUsage(null));
        dispatch(setUsageFrom(null));
        dispatch(setUsageTo(null));
        setValue("usage", null);
        setValue("usageFrom", null);
        setValue("usageTo", null);
        break;
      }
      case "Demand": {
        dispatch(setDemand(null));
        setValue("demand", null);
        break;
      }
      case "Load Factor":
      case "Load Factor (%)": {
        dispatch(setLoadFactor(null));
        dispatch(setLoadFactorFrom(null));
        dispatch(setLoadFactorTo(null));
        setValue("loadFactor", null);
        setValue("loadFactorFrom", null);
        setValue("loadFactorTo", null);
        break;
      }
      case "Product Type": {
        dispatch(setSelectedProductType([]));
        setValue("productType", []);
        break;
      }
      case "Pass Through": {
        // Reset Pass Through to default "All" when badge is cleared
        dispatch(setSelectedCostComponent(["All"]));
        selectedCostComponentsRef.current = ["All"];
        setValue("passThrough", [{ id: "All", name: "All", value: "All" }]);
        break;
      }
      case "Customer Class": {
        dispatch(setCustomerClass(""));
        setValue("customerType", { id: "All", name: "All", value: "All" });
        break;
      }
      default:
        return;
    }

    // re-fetch with updated filters
    if (accessToken && isPremium !== null) {
      if (isPremium === true) {
        const selectedClientIdentifier = getSelectedClientIdentifier();
        dispatch(
          fetchChartsData(
            clientId,
            selectedClientIdentifier,
            accessToken,
            false,
            isSystemAdmin,
            isPremium
          )
        );
      } else {
        applyNonPremiumStaticCharts();
      }
    }
  };
  // debugger;
  return (
    <>
      <div className="mb-2" style={{ marginTop: '1px' }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h4 className="my-2 py-0">Custom Pricing</h4>
          <div className="d-flex flex-wrap align-items-center gap-3">
            <div className="d-flex align-items-center gap-2">
              <span style={{ fontWeight: 800, fontSize: '0.7rem', whiteSpace: 'nowrap' }}>Saved Filters:</span>
              <Dropdown>
                <Dropdown.Toggle variant="link" className="p-0 shadow-none text-decoration-none" style={{ fontWeight: 600, fontSize: '0.8rem', color: '#6c757d', lineHeight: 1, paddingBottom: '1px' }}>
                  {savedFiltersList && savedFiltersList.length > 0 && selectedSavedFilterId
                    ? (savedFiltersList.find((f: any) => f.id === selectedSavedFilterId)?.name || "Default")
                    : "Default"}
                </Dropdown.Toggle>
                <Dropdown.Menu style={{ minWidth: '220px' }}>
                  <Dropdown.Item active={!selectedSavedFilterId} onClick={() => resetFilters()}>Default</Dropdown.Item>
                  {savedFiltersList && savedFiltersList.map((f: any) => (
                    <Dropdown.Item key={f.id} className="d-flex justify-content-between align-items-center" active={f.id === selectedSavedFilterId} onClick={() => applySavedFilter(f.id)}>
                      <span>{f.name}</span>
                      <span className="text-muted" style={{ cursor: "pointer", fontSize: "10px", lineHeight: 1, marginLeft: '8px' }} onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleDeleteSavedFilter(f.id); }} title="Delete filter">
                        <CloseIcon />
                      </span>
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Button size="sm" type="button" variant="primary" onClick={resetFilters} disabled={isPremium === false}>Reset</Button>
              <Button size="sm" type="button" variant="primary" onClick={() => { setShowSaveModal(true); setSaveFilterName(""); }} disabled={isPremium === false || savingFilters}>Save Filters</Button>
              <Button size="sm" type="button" variant="primary" onClick={() => handleSubmit(onSubmit)()} disabled={isPremium === false}>Apply</Button>
            </div>
          </div>
        </div>
      </div>
      {/* Save Filter Name Modal */}
      <ConfirmationModal
        dialogClassName="modal-md"
        show={showSaveModal}
        onHide={() => setShowSaveModal(false)}
        centered
        size="sm"
        title="Save Filter"
        confirmButtonLabel={savingFilters ? "Saving..." : "Save & Apply"}
        isLoading={savingFilters}
        onConfirm={() => {
          if (saveFilterName.trim()) {
            handleSaveFilters(saveFilterName.trim());
            setShowSaveModal(false);
            setSaveFilterName("");
          }
        }}
      >
          <FormInput type="text" name="saveFilterName" placeholder="Enter filter name" value={saveFilterName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSaveFilterName(e.target.value)} autoFocus />
      </ConfirmationModal>
      <Card>
        <Card.Body>
          <Row>
            <Col md={12}>
              {/* <StickyDiv>
              <SelectedFilters onDeleteFilter={onDeleteFilter}/>
            </StickyDiv> */}
              <AccordionFilter
                hide={() => {}}
                control={control}
                register={register}
                handleSubmit={handleSubmit(onSubmit)}
                errors={errors}
                reset={resetFilters}
                resetKey={effectiveResetKey}
                onDeleteFilter={onDeleteFilter}
                trigger={trigger}
                adminClients={availableAdminClients}
                dependantDropdowns={{
                  utilities: availableUtilities,
                  rateClasses: availableRateClasses,
                  loadProfiles: availableLoadProfiles,
                  loadZones: availableLoadZones,
                  billingMethods: availableBillingMethods,
                  states: availableStates,
                  markets: pricingState.markets,
                  customerTypes: availableCustomerTypes,
                  // productTypes:availableProductType
                }}
                dropdownLoading={dropdownLoading}
                setValue={setValue}
                filterActionsDisabled={isPremium === false}
                savedFilters={savedFiltersList}
                onSaveFilters={handleSaveFilters}
                onDeleteSavedFilter={handleDeleteSavedFilter}
                onApplySavedFilter={applySavedFilter}
                savingFilters={savingFilters}
                selectedSavedFilterId={selectedSavedFilterId}
              />
            </Col>
            <Col
              md={2}
              className="d-flex justify-content-end align-items-center mb-3"
            >
              {/* <StickyDiv>
                <Button
                variant="primary"
                className="mx-1"
                onClick={openFilterDrawer}
              >
                <FiltersIcon />
              </Button>
              </StickyDiv> */}
            </Col>
            <Col md={12}>
              <ChartSection></ChartSection>
              {/* {
              isFilterDrawerOpen && 
              (
                    <Drawer
                        isOpen={isFilterDrawerOpen}
                        hide={closeFilterDrawer}
                        content={
                            <PricingMatrixFilters
                                hide={closeFilterDrawer}
                                control={control}
                                register={register}
                                handleSubmit={handleSubmit(onSubmit)}
                                errors={errors}
                                reset={resetFilters}
                                dependantDropdowns={{
                                    utilities: availableUtilities,
                                    rateClasses: availableRateClasses,
                                    loadProfiles: availableLoadProfiles,
                                    loadZones: availableLoadZones,
                                    billingMethods: availableBillingMethods,
                                    states: availableStates,
                                    markets: pricingState.markets,
                                }}
                                // filters={filters}
                                // setFilters={setFilters}
                                // extra={extra}
                            />
                        }
                    />
                )} */}
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </>
  );
};

export default CustomPricingContent;
